import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Link, useNavigate, useOutletContext, useSearchParams } from 'react-router-dom'
import {
  Clock, Stethoscope, MapPin, Loader2, CalendarX, ChevronDown, EyeOff,
  Plus, RotateCcw, CalendarDays, History, Check, ExternalLink, ChevronLeft, ChevronRight,
  Tag, FileText, Search, Trash2, UserPlus, Ban, ClipboardList, ArrowRight, ArrowLeftRight,
} from 'lucide-react'
import { apiGet, cachedGet, patchJson, postJson } from '../../lib/api.js'
import { buildDocInfo, eligibleDoctorsFromTimetable, shiftEligible, doctorEligible } from '../../lib/doctorFilter.js'
import { nameInitial } from '../../lib/text.js'
import { isBookableType, treatmentLabel } from '../../lib/apptTypes.js'
import { useTypeahead } from '../../lib/useTypeahead.js'
import { statusTone, STATUS_TONES, STATUS_GROUPS, predictPatientStatus } from '../../data/patientStatuses.js'
import {
  R48, r48State, effectiveStatus, hasFutureBooking,
  apptStatusInfo, codeCls, accentOf, R48_EXPIRED_CLS, canSetApptStatus,
} from '../../data/appointmentStatuses.js'
import ApptStatusSelect from '../../components/ApptStatusSelect.jsx'
import PatientStatusSelect from '../../components/PatientStatusSelect.jsx'
import BookingCalendar from '../../components/BookingCalendar.jsx'
import CreateTreatmentButton from '../../components/CreateTreatmentButton.jsx'
import ApptLabFlag from '../../components/lab/ApptLabFlag.jsx'
import ApptLabPanel from '../../components/lab/ApptLabPanel.jsx'
import { buildApptLabIndex } from '../../lib/lab.js'
import { usePatientLive } from '../../lib/patientLive.js'
import { useMe } from '../../lib/me.js'
import { MIN_PATIENT_SEARCH } from '../../lib/patientSearch.js'
import UpsellTags, { upsellTags } from '../../components/UpsellTags.jsx'
import {
  PageHeader, Button, Tabs, ViewToggle, DataTable, useTableState, Checkbox,
  Drawer, DrawerFooter, Modal, ConfirmDialog, useToast,
  Loading, EmptyState, ErrorState, InlineError,
  Field, Input, Textarea, Select, SearchSelect, FIELD_CLASS,
  PatientLink, fmtDateText, fmtMonth, fmtTime, toISODate, bkkTodayISO,
  TH_DAYS_SHORT, formBranchOptions, branchName,
} from '../../kit'

// Status labels, badge colours, accent bars and the picker options all live in
// src/data/appointmentStatuses.js — this file used to keep its own copies and
// they drifted (Walk-in, ยังไม่ยืนยัน, a P* nobody had a label for).

export const EMPTY_LOOKUP = { map: {}, opts: [], byLabel: {} }
export const asList = (r) => (Array.isArray(r) ? r : (r?.items || []))
function msOf(v) {
  if (v == null || v === '') return null
  if (typeof v === 'number') return v
  if (/^\d+$/.test(String(v))) return Number(v)
  const d = new Date(v); return isNaN(d.getTime()) ? null : d.getTime()
}
function parseDateOnly(s) {
  const m = /^(\d{4})-(\d{1,2})-(\d{1,2})/.exec(String(s || ''))
  return m ? new Date(+m[1], +m[2] - 1, +m[3]) : null
}
const pad = (n) => String(n).padStart(2, '0')
// Asia/Bangkok = UTC+7, no DST. Render/edit in Bangkok wall-clock regardless of
// the machine's timezone: shift by +7h and read the UTC fields.
const BKK_OFFSET = 7 * 60 * 60 * 1000
// Display and 'YYYY-MM-DD' / 'HH:mm' reads go through kit/format.js (fmtTime,
// toISODate, bkkTodayISO); only the day-bucket helper below still needs this.
const bkkDate = (ms) => new Date(ms + BKK_OFFSET) // then use getUTC*
// Absolute ms for a given Bangkok wall-clock date+time.
const bkkToMs = (y, mo, d, hh, mm) => Date.UTC(y, mo - 1, d, hh || 0, mm || 0) - BKK_OFFSET
// A local-midnight Date carrying the Bangkok calendar date (for day buckets/labels).
export const bkkDayFromMs = (ms) => { const b = bkkDate(ms); return new Date(b.getUTCFullYear(), b.getUTCMonth(), b.getUTCDate()) }
export const dayKey = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
export { bkkToMs }

function timeRange(a) {
  const s = msOf(a.start_date)
  if (s == null) return ''
  const st = fmtTime(s)
  const e = msOf(a.end_date)
  const et = e != null ? fmtTime(e) : ''
  if (st === '00:00' && (et === '' || et === '00:00')) return ''
  return et && et !== st ? `${st}–${et}` : st
}
function apptDay(a) {
  const d = parseDateOnly(a.date)
  if (d) return d
  const ms = msOf(a.start_date)
  return ms != null ? bkkDayFromMs(ms) : null
}
function titleType(title, patientName) {
  let t = String(title || '').trim()
  if (patientName && t.startsWith(patientName)) t = t.slice(patientName.length).replace(/^\s*-\s*/, '')
  return t.replace(/\s+(C\*|C|P|X|W|D|N|\+)$/i, '').trim()
}
// Screen dates are ค.ศ. (kit/format.js) — only generated documents
// (DocumentGenerator, AppointmentPrint, WHT) still carry พ.ศ. The appointment day
// keeps its weekday: staff book by "which Tuesday". Passed as 'YYYY-MM-DD' so the
// kit reads it as a calendar day, never re-shifted by the browser's timezone.
const apptDayText = (d) => (d ? fmtDateText(dayKey(d), { weekday: true }) : '—')
// Branch name for display. A row still saved under สนามเป้า (#1) reads as อารีย์
// (kit/branches.js#branchName, decision #2). '' while the list is not loaded.
const lookupBranches = (lookup) => Object.entries(lookup?.map || {}).map(([id, name]) => ({ id, name }))
function branchLabel(id, lookup) {
  const list = lookupBranches(lookup)
  return Number(id) > 0 && list.length ? branchName(id, list) : ''
}

export default function PatientAppointments() {
  const { patient, setPatient, live } = useOutletContext() || {}
  const id = patient?.id
  const patientName = (patient?.name || '').trim()

  /**
   * Push a patient status this page CAUSED into the shared patient row, which
   * PatientLayout owns and the sticky header pill reads.
   *
   * 🚨 Do not leave this to realtime. Two statuses are caused from right here —
   * Xano trigger 2's "Error" when a save takes away the last future booking, and
   * whatever staff then pick in the follow-up prompt — and until 2026-09-12 both
   * were left to the `patient_update/{id}` socket to report back. The header kept
   * the old pill (reported that day: "after set, the patient page does not follow
   * up"), while the server was provably right: trigger 28 ran and rewrote the row.
   * A write we made ourselves must not need a round trip to show on our own
   * screen — the header's own StatusControl has always worked this way.
   */
  const applyPatientStatus = useCallback((s) => {
    if (typeof s !== 'string') return
    setPatient?.((p) => (p && String(p.id) === String(id) ? { ...p, status: s } : p))
  }, [setPatient, id])

  const me = useMe()
  const toast = useToast()
  const navigate = useNavigate()
  const [rows, setRows] = useState(null)
  // Treatments this patient already has, indexed by the appointment they came
  // from — drives the ✓ link and the "already has one" confirm. Loaded lazily
  // with the ประวัติ tab, since the default tab never renders it.
  const [txByAppt, setTxByAppt] = useState(null)
  // Which appointments have lab work around them, and whether it is in yet
  // (lib/lab.js#buildApptLabIndex). Eagerly loaded, unlike txByAppt: the flag
  // belongs on the UPCOMING block too — a crown that has not arrived for a visit
  // three days out is the whole point of showing it.
  const [labIndex, setLabIndex] = useState(null)
  const [branch, setBranch] = useState(EMPTY_LOOKUP)
  // Dropdown lists for the edit modal — lazy-loaded on first edit/new (not on view).
  const [editData, setEditData] = useState({ type: EMPTY_LOOKUP, doctor: EMPTY_LOOKUP, docInfo: {}, loaded: false, loading: false })
  const [error, setError] = useState(null)
  const [editing, setEditing] = useState(null)
  const [creating, setCreating] = useState(false)
  const [pickedSlot, setPickedSlot] = useState(null) // {date, time} chosen on the calendar
  const [calRefresh, setCalRefresh] = useState(0)    // bump → calendar re-fetches in place (keeps view/filter)
  const [searchParams] = useSearchParams()
  // หานัดคนไข้ is the default (2026-08-16). It used to be ประวัติ, on the
  // reasoning that staff read the record more often than they book — but
  // นัดหมายที่จะถึง now sits ABOVE the tabs and is visible either way, so the
  // record is no longer something you have to pick a tab to see. What is left
  // behind each tab is the booking calendar and the past list, and booking is
  // the thing you came to do. `?tab=past` still lands on the history.
  const [tab, setTab] = useState(searchParams.get('tab') === 'past' ? 'past' : 'upcoming') // 'upcoming' | 'past'
  // ตาราง/การ์ด for BOTH lists — the upcoming block above the tabs and the past
  // list inside ประวัติ. The toggle itself only shows on ประวัติ, so switching it
  // there also re-renders the block above; that is intended, they are one list
  // split by time, not two unrelated tables.
  const [view, setView] = useState('table')

  // Display needs only appointments (type via embedded _appointmentList, doctor
  // embedded) + branches (for the branch name). Two calls instead of four.
  const load = useCallback((silent = false) => {
    if (!id) return
    if (!silent) setRows(null) // silent reload keeps the calendar mounted (preserves its view/filter)
    setError(null)
    Promise.allSettled([
      apiGet('/api/appointments', { patient_id: id, type: 'patient' }),
      cachedGet('/api/branches'),
    ]).then(([ap, br]) => {
      if (ap.status === 'fulfilled') {
        setRows(asList(ap.value).filter((a) => String(a.patient_id) === String(id) && a.active !== false))
      } else { setError(ap.reason?.message || 'โหลดนัดหมายไม่สำเร็จ'); setRows([]) }
      setBranch(buildLookup(br, (b) => b.name || b.branch_name))
    })
  }, [id])

  // Which appointments already produced a treatment. `type=patient` already
  // filters active==true server-side, so anything here is a live treatment.
  const loadTx = useCallback(() => {
    if (!id) return
    apiGet('/api/treatments', { patient_id: id, type: 'patient', per_page: 100 })
      .then((r) => {
        const m = {}
        for (const t of asList(r)) {
          const aid = String(t.appointment_id || '')
          if (!aid || aid === '0') continue
          ;(m[aid] || (m[aid] = [])).push(t)
        }
        setTxByAppt(m)
      })
      .catch(() => setTxByAppt({})) // a failed index must not hide the buttons
  }, [id])

  // Every lab this patient has ever had — GET /lab with no ปี/เดือน returns their whole
  // history, which for one patient is a handful of rows. Indexed on BOTH the explicit
  // appointment_id and patient+day, because only ~24% of labs carry the link
  // (lib/lab.js#buildApptLabIndex has the measurement).
  const loadLabs = useCallback(() => {
    if (!id) return
    apiGet('/api/lab', { patient_id: id, per_page: 200 })
      .then((r) => setLabIndex(buildApptLabIndex(asList(r))))
      .catch(() => setLabIndex(buildApptLabIndex([])))  // no flag beats a wrong flag
  }, [id])

  // Lazy-load the dropdown lists (doctors + appointment types) once, for the
  // edit / new modal.
  const ensureEditData = useCallback(() => {
    setEditData((prev) => {
      if (prev.loaded || prev.loading) return prev
      Promise.allSettled([cachedGet('/api/doctors'), cachedGet('/api/appointment-types')]).then(([dr, ty]) => {
        setEditData({
          doctor: buildLookup(dr, (d) => d.code || [d.name, d.surname].filter(Boolean).join(' ')),
          docInfo: buildDocInfo(dr.status === 'fulfilled' ? asList(dr.value) : []),
          type: buildTypeLookup(ty),
          loaded: true, loading: false,
        })
      })
      return { ...prev, loading: true }
    })
  }, [])
  const openEdit = useCallback((a) => { ensureEditData(); setEditing(a) }, [ensureEditData])
  const openCreate = useCallback(() => { ensureEditData(); setCreating(true) }, [ensureEditData])

  // A treatment (+ its chart) was just created from an appointment: re-index and
  // offer the jump straight into the new chart, which is where staff go next.
  const onTxCreated = useCallback((res) => {
    loadTx()
    toast.success('สร้างการรักษาเรียบร้อยแล้ว', res?.treatment_id ? {
      action: { label: 'เปิด chart', onClick: () => navigate(`/patient/${id}/chart/history/${res.treatment_id}`) },
      duration: 8000, // long enough to reach the button — the chart is where staff go next
    } : undefined)
  }, [loadTx, id, toast, navigate])

  useEffect(() => { load() }, [load])
  useEffect(() => { loadLabs() }, [loadLabs])
  // Only the ประวัติ tab renders the treatment column, and the treatments call
  // is the heavy one (billing + lab addons per row) — don't pay for it on หานัด.
  useEffect(() => { if (tab === 'past') loadTx() }, [tab, loadTx])

  // Live: the appointment list itself (booked / moved / status changed / cancelled
  // at another station, or in the Bubble app) and treatments (a treatment created
  // from one of these appointments flips the row's ✓).
  //
  // Until 2026-08-18 this listened to `treatment_update` alone, so the ประวัติ list
  // never moved when the appointment itself changed — POST /appointment/status
  // broadcast to a broken channel suffix and DELETE /appointment broadcast nothing.
  // The `patient_update - appointment` table trigger covers all of it.
  usePatientLive(live, ['appointment', 'treatment'], () => { loadTx(); load(true) })

  const { upcoming, past } = useMemo(() => {
    const list = (rows || []).map((a) => ({ a, day: apptDay(a) })).filter((x) => x.day)
    const today = bkkTodayISO() // "today" in Bangkok
    const up = [], pa = []
    for (const x of list) (dayKey(x.day) >= today ? up : pa).push(x)
    up.sort((p, q) => p.day - q.day)
    pa.sort((p, q) => q.day - p.day)
    return { upcoming: up, past: pa }
  }, [rows])

  // Appointment-outcome tally across this patient's whole record:
  //   ยืนยัน = C/C* · เลื่อน = P/P* · ไม่มา/อื่นๆ = X, W, U and every other status.
  // R48 resolves through effectiveStatus() first: a live hold counts as ยืนยัน,
  // a lapsed one becomes W (รอติดต่อ) and lands in ไม่มา/อื่นๆ.
  const stats = useMemo(() => {
    let confirm = 0, postpone = 0, noshow = 0
    for (const a of rows || []) {
      const s = effectiveStatus(a)
      if (s === 'C' || s === 'C*') confirm++
      else if (s === 'P' || s === 'P*') postpone++
      else noshow++
    }
    return { confirm, postpone, noshow }
  }, [rows])

  // Patient's previous doctors (unique, with name + profile image + visit count)
  // → ทันตแพทย์เดิม filter, sorted by how often the patient saw each doctor.
  const prevDoctors = useMemo(() => {
    const m = new Map()
    for (const a of rows || []) {
      const did = String(a.doctor_id)
      if (!did || did === '0') continue // 0 = no doctor; doctor_id 1 = เอก ธนิตย์ (real)
      const cur = m.get(did)
      if (cur) cur.count++
      else m.set(did, {
        id: did,
        name: a.doctor?.code || [a.doctor?.name, a.doctor?.surname].filter(Boolean).join(' ') || `ทันตแพทย์ #${did}`,
        img: a.doctor?.profile_img || '',
        count: 1,
      })
    }
    return [...m.values()].sort((x, y) => y.count - x.count || x.name.localeCompare(y.name, 'th'))
  }, [rows])

  // อารีย์ branch id (fallback when the patient's home branch is สนามเป้า #1).
  const areeBranchId = useMemo(() => {
    const m = branch.map || {}
    return Object.keys(m).find((bid) => (m[bid] || '').includes('อารีย์')) || ''
  }, [branch])

  if (!id) return null
  const ctx = { branch, patientName, onEdit: openEdit, patientId: id, txByAppt, onTxCreated, labIndex }
  const blankAppt = (() => {
    let start_date = null, date = bkkTodayISO()
    if (pickedSlot) {
      const [y, mo, d] = pickedSlot.date.split('-').map(Number)
      const [hh, mm] = pickedSlot.time.split(':').map(Number)
      start_date = bkkToMs(y, mo, d, hh, mm)
      date = pickedSlot.date
    }
    const branch_id = pickedSlot?.branchSel ? Number(pickedSlot.branchSel)
      : (String(patient?.branch_id) === '1' && areeBranchId) ? Number(areeBranchId)
      : (patient?.branch_id ?? null)
    return { patient_id: id, status: '', appointmentlist_id: pickedSlot?.treatId ? Number(pickedSlot.treatId) : null, doctor_id: pickedSlot?.doctorId ? Number(pickedSlot.doctorId) : null, branch_id, date, start_date, end_date: null, duration: Number(pickedSlot?.dur) || 30, details: '', remark: '', active: true }
  })()

  return (
    <div>
      {/* Inside PatientLayout: a level-2 header, no <Page> wrapper. */}
      <PageHeader level={2} title="นัดหมาย" subtitle="คลิกที่นัดหมายเพื่อแก้ไข"
        meta={rows ? `ทั้งหมด ${rows.length} รายการ` : null}
        actions={<Button icon={Plus} onClick={openCreate}>นัดหมายใหม่</Button>}
        className="mb-4" />

      {/* Outcome stats — this patient's whole appointment record */}
      {rows && rows.length > 0 && (
        <div className="mb-4 flex flex-wrap gap-2">
          <StatChip label="ยืนยัน" value={stats.confirm} tone="emerald" />
          <StatChip label="เลื่อนนัด" value={stats.postpone} tone="amber" />
          <StatChip label="ไม่มา/อื่นๆ" value={stats.noshow} tone="rose" />
        </div>
      )}

      {rows === null && <Loading label="กำลังโหลดนัดหมาย…" />}

      {/* นัดหมายที่จะถึง — ABOVE the tabs, so it is on screen whichever one is
          open (2026-08-16). It used to live inside ประวัติ, which meant the one
          question every counter asks — "does this patient already have a next
          appointment?" — was invisible on the tab where you book the next one,
          and staff double-booked. An empty list is information too, so it always
          renders. A failed load shows the error here instead of "empty". */}
      {rows && (
        <div className="mb-5">
          <Section title="นัดหมายที่จะถึง" count={upcoming.length} accent view={view} items={upcoming} ctx={ctx} upcoming
            error={error} onRetry={() => load()} empty="ไม่มีนัดหมายที่จะถึง" />
        </div>
      )}

      <Tabs className="mb-4" value={tab} onChange={setTab} items={[
        // หานัดคนไข้ first — it is the default tab, and the leading tab should
        // be the one the page actually opens on.
        { key: 'upcoming', icon: CalendarDays, label: 'หานัดคนไข้' },
        // Counts the PAST list only now that upcoming has its own block above;
        // a badge that included the appointments shown elsewhere read as a
        // total nobody could reconcile with what was under the tab.
        { key: 'past', icon: History, label: 'ประวัติ', badge: past.length },
      ]} />

      {rows && (
        <>
          {/* หานัดคนไข้ — the calendar owns its own view toggle (shown only after a day is picked) */}
          {tab === 'upcoming' && (
            <BookingCalendar
              branchId={patient?.branch_id}
              prevDoctors={prevDoctors}
              liveUpdates
              refreshKey={calRefresh}
              onPickSlot={(date, time, treatId, doctorId, branchSel, dur) => { ensureEditData(); setPickedSlot({ date, time, treatId, doctorId, branchSel, dur }); setCreating(true) }}
              onEditAppt={(raw) => openEdit(raw)}
            />
          )}

          {/* ประวัติ — what has already happened. The upcoming half of this list
              now renders above the tabs, so this tab is purely the past. The
              ตาราง/การ์ด switch sits on this list's header, and also redraws the
              upcoming block above: they are one list split by time. */}
          {tab === 'past' && (
            <Section title="ประวัตินัดหมาย (ผ่านมาแล้ว)" count={past.length} view={view} items={past} ctx={ctx}
              error={error} onRetry={() => load()} empty="ยังไม่มีประวัตินัดหมาย"
              aside={<ViewToggle value={view} onChange={setView} views={['table', 'card']} />} />
          )}
        </>
      )}

      {editing && (
        <ApptEditDrawer
          mode="edit" a={editing} branch={branch} type={editData.type} docInfo={editData.docInfo}
          existingCount={(txByAppt?.[String(editing.id)] || []).length}
          // This page already loaded the patient's labs for the row flags — reuse them
          // (undefined on /appointments, where the panel fetches its own).
          labIndex={labIndex}
          // Already fetched by this page — lets the drawer predict the patient's
          // resulting status locally, with no extra request.
          siblings={rows}
          onTreatmentCreated={(res) => { setEditing(null); onTxCreated(res) }}
          onClose={() => setEditing(null)}
          onPatientStatus={applyPatientStatus}
          onSaved={() => { setEditing(null); load(true); setCalRefresh((k) => k + 1) }}
        />
      )}
      {creating && (
        <ApptEditDrawer
          mode="create" a={blankAppt} branch={branch} type={editData.type} docInfo={editData.docInfo}
          siblings={rows}
          onPatientStatus={applyPatientStatus}
          onClose={() => { setCreating(false); setPickedSlot(null) }}
          // The drawer itself toasts the success (it is shared with /appointments).
          onCreate={() => { setCreating(false); setPickedSlot(null); load(true); setCalRefresh((k) => k + 1) }}
        />
      )}
    </div>
  )
}

/* -------------------------------------------------------------------------- */
/* Month calendar — marks days that have appointments, click to filter.       */
/* (Slot-finding / booking logic comes later.)                                */

export function buildLookup(res, labelFn) {
  const map = {}, byLabel = {}, opts = []
  if (res?.status === 'fulfilled') {
    for (const x of asList(res.value)) {
      const xid = String(x.id ?? x.branch_id ?? x.doctor_id ?? '')
      const label = (labelFn(x) || '').trim()
      if (!xid) continue
      map[xid] = label
      if (label && !byLabel[label]) { byLabel[label] = xid; opts.push(label) }
    }
  }
  return { map, opts, byLabel }
}

function Section({ title, count, accent, view, items, ctx, upcoming, empty, error, onRetry, aside }) {
  return (
    <div>
      <div className="flex items-center gap-2 mb-2.5 min-h-[32px]">
        <span className={`text-[12.5px] uppercase tracking-[0.14em] font-semibold ${accent ? 'text-brand-600' : 'text-slate-500'}`}>{title}</span>
        <span className="text-[12.5px] font-medium text-slate-400 bg-slate-100 rounded-full px-1.5 py-0.5">{count}</span>
        {aside && <div className="ml-auto">{aside}</div>}
      </div>
      {view === 'table'
        ? <ApptTable items={items} ctx={ctx} error={error} onRetry={onRetry} empty={empty} />
        : error
          ? <ErrorState message={error} onRetry={onRetry} />
          : items.length === 0
            ? <EmptyState icon={CalendarX} title={empty || 'ไม่มีรายการ'} />
            : <div className="space-y-2.5">{items.map(({ a }, i) => <ApptCard key={a.id ?? i} a={a} ctx={ctx} upcoming={upcoming} />)}</div>}
    </div>
  )
}

// ประเภท + detail from the embedded _appointmentList → { main, detail }.
// main = the type code (e.g. GP / Ortho / Prosth); detail = its description.
function apptType(a, patientName) {
  const al = a._appointmentList
  if (al && typeof al === 'object') {
    const main = (al.type || al.name || al.code || al.label || al.title || al.value || '').toString().trim()
    const detail = (al.detail || al.description || al.desc || al.name_th || al.nameTH || '').toString().trim()
    if (main || detail) {
      return main ? { main, detail: detail && detail !== main ? detail : '' } : { main: detail, detail: '' }
    }
  }
  return { main: titleType(a.title, patientName) || 'นัดหมาย', detail: '' }
}

// Appointment-type lookup, grouped for a type → detail cascade.
//   byId[id]    = { type, detail, duration }
//   types       = distinct type names (in order)
//   byType[type]= [{ id, detail, duration }]
export function buildTypeLookup(res) {
  const byId = {}, byType = {}, types = []
  if (res?.status === 'fulfilled') {
    for (const t of asList(res.value)) {
      const tid = String(t.id ?? '')
      if (!tid) continue
      const type = ((t.type || t.name || t.code || t.label || t.value || '').toString().trim()) || '—'
      const detail = (t.detail || t.description || t.desc || '').toString().trim()
      const duration = Number(t.duration ?? t.default_duration ?? t.duration_default ?? t.minutes ?? 0) || 0
      // byId stays COMPLETE — it resolves appointmentlist_id → label for rows that
      // already exist, including ones on a retired type. Only byType (the type →
      // detail cascade staff pick from) skips retired rows. lib/apptTypes.js.
      byId[tid] = { type, detail, duration }
      if (!isBookableType(t)) continue
      if (!byType[type]) { byType[type] = []; types.push(type) }
      byType[type].push({ id: tid, detail: detail || type, duration })
    }
  }
  return { byId, byType, types }
}

function cellData(a, ctx) {
  const day = apptDay(a)
  return {
    day,
    type: apptType(a, ctx.patientName),
    time: timeRange(a),
    doctor: (a.doctor && (a.doctor.code || [a.doctor.name, a.doctor.surname].filter(Boolean).join(' '))) || '',
    branch: branchLabel(a.branch_id, ctx.branch),
    detail: (a.details ?? '').toString().trim(),
    upsell: upsellTags(a), // what the patient said สนใจ to on the SMS page
    st: a.status ? apptStatusInfo(a.status) : null,
    r48: r48State(a),
  }
}

// What the "สร้างการรักษา" confirm shows: LABELLED fields, not a run of
// concatenated lines — "ทันตแพทย์ เค้ก พรรณราย · Tooth Taxi" read as though the branch
// were part of the doctor's name. Exported; the edit drawer uses it too.
export function apptSummaryFields(a, ctx = {}) {
  const c = cellData(a, { branch: ctx.branch || EMPTY_LOOKUP, patientName: ctx.patientName || '' })
  const fields = [
    { label: 'วันที่', value: [apptDayText(c.day), c.time && `${c.time} น.`].filter(Boolean).join(' · ') },
    { label: 'รายการ', value: [c.type.main, c.type.detail].filter(Boolean).join(' · ') },
    { label: 'ทันตแพทย์', value: c.doctor },
    { label: 'สาขา', value: c.branch },
  ].filter((f) => f.value)

  const warnings = []
  // A treatment is dated by its APPOINTMENT, and that date drives the doctor's
  // monthly fee window — so name the date it will actually land on.
  const day = c.day ? dayKey(c.day) : ''
  if (day && day !== bkkTodayISO()) {
    warnings.push(`ไม่ใช่นัดของวันนี้ — การรักษาจะลงวันที่ ${apptDayText(c.day)}`)
  }
  // The button is offered on every row, so a cancelled / no-show appointment has
  // to say so here — a treatment normally means the patient turned up.
  // X carries BOTH meanings (Ram, 2026-09-10), so the warning names both. The old
  // `status === 'N'` branch beside this one was unreachable — N is not in table 20's
  // enum and 0 rows have ever held it — and is gone.
  if (a.status === 'X') warnings.push('นัดนี้ถูกยกเลิก / ไม่มาตามนัด — ปกติไม่ต้องสร้างการรักษา')
  return { fields, warnings }
}

// การรักษา cell/action. The button is on EVERY row; what changes is its colour
// and the dialog behind it — amber + an "already created" warning once this
// appointment has a treatment, brand-blue + the normal confirm when it doesn't.
// Alongside it, one minimized chart icon per existing treatment.
function TreatmentAction({ a, ctx, variant = 'row' }) {
  const list = ctx.txByAppt?.[String(a.id)] || []
  // Until the index lands every row would read as "no treatment yet" (blue) —
  // the wrong colour is worse than no button, so hold it disabled until we know.
  const pending = ctx.txByAppt == null
  const icon = variant === 'icon'
  return (
    <span className="inline-flex items-center gap-1.5">
      <CreateTreatmentButton
        appointmentId={a.id}
        existingCount={list.length}
        disabled={pending}
        variant={variant}
        {...apptSummaryFields(a, ctx)}
        onCreated={ctx.onTxCreated}
      />
      {list.map((t) => (
        <Link key={t.id} to={`/patient/${ctx.patientId}/chart/history/${t.id}`}
          title={`เปิด chart ของการรักษา #${t.id}`}
          className={`inline-flex items-center justify-center rounded-lg text-emerald-600 bg-emerald-50 ring-1 ring-emerald-200 hover:bg-emerald-100 transition-colors ${icon ? 'w-7 h-7' : 'w-6 h-6'}`}>
          <ClipboardList size={icon ? 14 : 13} />
        </Link>
      ))}
      {/* No chart yet: hold the slot with a ghost icon rather than leaving a gap,
          so the action column is the same shape on every row and the absence of
          a chart is something you can see instead of something you infer. Not a
          link and not focusable — there is nothing to open. */}
      {list.length === 0 && (
        <span aria-hidden="true" title="ยังไม่มี chart — ต้องสร้างการรักษาก่อน"
          className={`inline-flex items-center justify-center rounded-lg text-slate-200 bg-slate-50/70 ring-1 ring-slate-100 ${icon ? 'w-7 h-7' : 'w-6 h-6'}`}>
          <ClipboardList size={icon ? 14 : 13} />
        </span>
      )}
    </span>
  )
}

// Status code pill — shared by the table and the cards.
function StatusCode({ a, c }) {
  return (
    <span title={[c.st?.label || a.status, c.r48.is && c.r48.hint].filter(Boolean).join(' · ')}
      className={`inline-flex items-center justify-center min-w-[30px] px-1.5 py-0.5 rounded-md text-[12.5px] font-bold ring-1 ${c.r48.expired ? R48_EXPIRED_CLS : codeCls(a.status)}`}>{a.status}</span>
  )
}

// Rows are `{ a, day }` (the page's upcoming / past split). Fixed column widths
// + DataTable `fixed`: the upcoming table and the history table are two separate
// tables, so without them each sized its columns from its own rows and the two
// grids never lined up. รายละเอียด has no width — it takes what is left.
const apptColumns = (ctx) => [
  {
    key: 'date', label: 'วันที่', width: '150px', sortable: true, defaultDir: 'desc',
    sortValue: ({ a, day }) => msOf(a.start_date) ?? (day ? day.getTime() : null),
    // date and time in one column — the เวลา column cost 110px that รายละเอียด
    // needed more. Duration is a bare (15), not "15 นาที". The status colour is
    // the row's accent bar (DataTable rowAccent), not drawn in this cell.
    render: ({ a, day }) => {
      const time = timeRange(a)
      return (
        <div className="whitespace-nowrap">
          <div className="text-slate-700">{apptDayText(day)}</div>
          <div className="text-[12.5px] text-slate-400 leading-tight tabular-nums"
            title={Number(a.duration) > 0 ? `${a.duration} นาที` : undefined}>
            {time || '—'}{Number(a.duration) > 0 && ` (${a.duration})`}
          </div>
        </div>
      )
    },
  },
  {
    key: 'type', label: 'ประเภท', width: '17%', sortable: true,
    sortValue: ({ a }) => apptType(a, ctx.patientName).main,
    render: ({ a }) => {
      const t = apptType(a, ctx.patientName)
      return (
        <>
          <div className="font-medium text-slate-800">{t.main}</div>
          {t.detail && <div className="text-[12.5px] text-slate-400 leading-tight">{t.detail}</div>}
        </>
      )
    },
  },
  {
    key: 'status', label: 'สถานะ', width: '84px', sortable: true,
    sortValue: ({ a }) => a.status || '',
    render: ({ a }) => {
      if (!a.status) return <span className="text-slate-300">—</span>
      const c = { st: apptStatusInfo(a.status), r48: r48State(a) }
      return (
        <>
          <StatusCode a={a} c={c} />
          {c.r48.is && <div className={`mt-0.5 text-[11px] leading-none ${c.r48.expired ? 'text-slate-400' : 'text-amber-600'}`}>{c.r48.hint}</div>}
        </>
      )
    },
  },
  {
    key: 'doctor', label: 'ทันตแพทย์', width: '16%', sortable: true,
    sortValue: ({ a }) => cellData(a, ctx).doctor,
    render: ({ a }) => {
      const doctor = cellData(a, ctx).doctor
      return doctor
        ? <span className="flex items-center gap-2 min-w-0"><DocAvatar img={a.doctor?.profile_img} name={doctor} size={22} /><span className="truncate" title={doctor}>{doctor}</span></span>
        : '—'
    },
  },
  {
    key: 'branch', label: 'สาขา', width: '10%', sortable: true, hideBelow: 'md',
    sortValue: ({ a }) => branchLabel(a.branch_id, ctx.branch),
    render: ({ a }) => { const b = branchLabel(a.branch_id, ctx.branch); return <span className="block truncate" title={b}>{b || '—'}</span> },
  },
  {
    key: 'detail', label: 'รายละเอียด',
    render: ({ a }) => {
      const upsell = upsellTags(a)
      return (
        <>
          {((a.details || '').trim() || upsell.length === 0) && <div className="text-slate-600">{(a.details || '').trim() || '—'}</div>}
          {(a.remark || '').trim() && <div className="text-[12.5px] text-slate-400 leading-tight mt-0.5">{(a.remark || '').trim()}</div>}
          {/* Lab flag sits with the promotion tags, not against ประเภท: both
              are "extra facts about this visit". empty:hidden — both children
              render null when they have nothing to say, and most rows have
              neither; without it every such row would carry an invisible margin. */}
          <div className="mt-1 flex items-center gap-1.5 flex-wrap empty:hidden">
            <ApptLabFlag index={ctx.labIndex} appt={a} size="sm" />
            <UpsellTags tags={upsell} />
          </div>
        </>
      )
    },
  },
  {
    key: 'tx', label: 'การรักษา', width: '120px',
    // The row itself opens the edit drawer — keep clicks AND keys in here local
    // (DataTable rows open on Enter/Space too).
    render: ({ a }) => (
      <span className="inline-flex whitespace-nowrap" onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()}>
        <TreatmentAction a={a} ctx={ctx} variant="icon" />
      </span>
    ),
  },
]

function ApptTable({ items, ctx, error, onRetry, empty }) {
  const columns = useMemo(() => apptColumns(ctx), [ctx])
  // No initial sort: the page hands the rows in already ordered (upcoming
  // soonest-first, history newest-first). Clicking a header re-sorts.
  const t = useTableState(items, columns, { pageSize: 50 })
  return (
    <DataTable columns={columns} {...t.tableProps} minWidth="860px" fixed
      rowKey={({ a }, i) => a.id ?? i}
      rowAccent={({ a }) => accentOf(a.status)}
      onRowClick={({ a }) => ctx.onEdit(a)}
      error={error} onRetry={onRetry}
      empty={{ title: empty || 'ไม่มีรายการ' }} />
  )
}

function ApptCard({ a, ctx, upcoming }) {
  const c = cellData(a, ctx)
  return (
    // The card body is one big button, so the การรักษา action lives in a
    // sibling footer — a button inside a button is invalid and un-clickable.
    <div className={`bg-white border rounded-2xl overflow-hidden ${upcoming ? 'border-brand-200/70 ring-1 ring-brand-100/60' : 'border-slate-200/80'}`}
      style={{ borderLeft: `5px solid ${accentOf(a.status)}` }}>
    <button type="button" onClick={() => ctx.onEdit(a)}
      className="w-full text-left p-4 pl-3 flex gap-4 hover:bg-slate-50/60 transition-colors">
      <div className="flex-none w-14 text-center">
        <div className="text-[12.5px] text-slate-400 leading-tight">{c.day ? TH_DAYS_SHORT[c.day.getDay()] : '—'}</div>
        <div className="text-[22px] font-bold text-slate-800 leading-tight tabular-nums">{c.day ? c.day.getDate() : '–'}</div>
        <div className="text-[12.5px] text-slate-400 leading-tight">{c.day ? fmtMonth(dayKey(c.day)) : ''}</div>
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-[15px] font-semibold text-slate-800">{c.type.main}</span>
          {c.type.detail && <span className="text-[13.5px] text-slate-400">· {c.type.detail}</span>}
          {a.status && <StatusCode a={a} c={c} />}
          {c.r48.is && <span className={`text-[12px] font-medium ${c.r48.expired ? 'text-slate-400' : 'text-amber-600'}`}>{c.r48.hint}</span>}
          {a.from &&<span className="text-[12px] font-medium text-slate-500 bg-slate-100 rounded px-1.5 py-0.5">{a.from}</span>}
        </div>
        <div className="mt-1.5 flex items-center gap-x-4 gap-y-1 flex-wrap text-[14px] text-slate-600">
          {c.time && <span className="inline-flex items-center gap-1.5"><Clock size={13} className="text-slate-400" />{c.time} น.</span>}
          {c.doctor && <span className="inline-flex items-center gap-1.5"><Stethoscope size={13} className="text-slate-400" />{c.doctor}</span>}
          {c.branch && <span className="inline-flex items-center gap-1.5"><MapPin size={13} className="text-slate-400" />{c.branch}</span>}
        </div>
        {c.detail && <div className="mt-2 text-[14px] text-slate-600 bg-slate-50/70 rounded-lg px-3 py-2 whitespace-pre-line">{c.detail}</div>}
        {/* With the promotion tags, matching the table's รายละเอียด column. */}
        <div className="mt-2 flex items-center gap-1.5 flex-wrap empty:hidden">
          <ApptLabFlag index={ctx.labIndex} appt={a} />
          <UpsellTags tags={c.upsell} />
        </div>
      </div>
    </button>
    <div className="border-t border-slate-100 px-4 py-2 flex items-center gap-2">
      <span className="text-[12.5px] text-slate-400">การรักษา</span>
      <TreatmentAction a={a} ctx={ctx} />
    </div>
    </div>
  )
}

/* -------------------------------------------------------------------------- */
/* Edit drawer (right-side slide-over)                                        */
/* -------------------------------------------------------------------------- */
const MINUTES = ['00', '15', '30', '45']
const SOURCES = ['ต่อเนื่อง', 'Facebook', 'LINE', 'Website', 'Phone', 'Walk-in', 'Tiktok', 'เพื่อนแนะนำ', 'Recall', 'Tooth Taxi']
const HOURS = Array.from({ length: 14 }, (_, i) => String(i + 8).padStart(2, '0')) // 08–21
const DURATIONS = [15, 30, 45, 60, 75, 90, 105, 120] // 15-min steps

function initialDraft(a, type) {
  const startMs = msOf(a.start_date)
  const cur = type.byId?.[String(a.appointmentlist_id)] || {}
  const hm = startMs != null ? fmtTime(startMs) : '09:00'
  // ประเภท has to be a REAL appointmentList type, because รายละเอียด is
  // byType[typeName]. apptType()'s 'นัดหมาย' fallback is a display label for
  // rows with no appointmentList — there is no such row in the catalog, so
  // seeding the form with it selects a type that owns zero details and leaves
  // รายละเอียด empty and disabled. Only the embedded record may seed it; with
  // neither, leave it blank so the placeholder shows and the cascade works.
  const embedded = (a._appointmentList && typeof a._appointmentList === 'object') ? apptType(a, '').main : ''
  return {
    status: a.status || '',
    typeName: cur.type || embedded || '',
    appointmentlist_id: a.appointmentlist_id != null ? String(a.appointmentlist_id) : '',
    doctor_id: a.doctor_id != null ? String(a.doctor_id) : '',
    branch_id: a.branch_id != null ? String(a.branch_id) : '',
    date: a.date || (startMs != null ? toISODate(startMs) : ''),
    hh: hm.slice(0, 2), mm: hm.slice(3, 5),
    duration: Number(a.duration) || 30,
    from: a.from || 'ต่อเนื่อง',
    upsale: !!a.upsale,
    extraQue: !!a.extraQue,
    details: a.details || '',
    remark: a.remark || '',
  }
}

export function ApptEditDrawer({ a, branch, type, docInfo = {}, mode = 'edit', existingCount = 0, siblings = null, labIndex, onClose, onSaved, onCreate, onTreatmentCreated, onPatientStatus }) {
  const isCreate = mode === 'create'
  const toast = useToast()
  const startMs = msOf(a.start_date)
  const [draft, setDraft] = useState(() => initialDraft(a, type))
  // Has staff changed anything? Drives the Drawer's "ทิ้งการแก้ไข?" guard. A flag
  // rather than a draft-vs-initial diff, because the drawer fills fields in BY
  // ITSELF after opening (ประเภท once the catalog lands, the Tooth Taxi start
  // time) and those are not edits anyone would lose.
  const [touched, setTouched] = useState(false)
  const edit = (fn) => { setDraft(fn); setTouched(true) }
  const [saving, setSaving] = useState(false)
  const [err, setErr] = useState(null)
  const [ttRows, setTtRows] = useState([])      // that day's timetable (for doctor filtering)
  const [ttLoading, setTtLoading] = useState(false)
  const [patientInfo, setPatientInfo] = useState(null) // full patient (edit mode) — for the header card
  const [pickedPatient, setPickedPatient] = useState(null) // main-page create: chosen via search
  const patientId = a.patient_id || pickedPatient?.id     // whom this appointment is for
  const canModeSelect = isCreate && !a.patient_id         // clinic-schedule create → mode chooser
  const [entryMode, setEntryMode] = useState('existing')  // 'existing' | 'new' | 'block'
  const editingBlock = !isCreate && a.block === true      // editing an existing doctor block
  const isBlock = (canModeSelect && entryMode === 'block') || editingBlock
  const [confirmDel, setConfirmDel] = useState(false)     // edit: soft-delete confirm
  const [deleting, setDeleting] = useState(false)
  // Set when this save took away the patient's LAST future booking — see finishSave().
  const [statusPrompt, setStatusPrompt] = useState(false)
  // The patient status chosen in the สถานะคนไข้ panel ('' = not touched) — see patientOutlook.
  const [patientPick, setPatientPick] = useState('')
  // The patient's own appointments when the caller has none to hand (clinic calendar):
  // null = loading, false = the fetch failed.
  const [ownRows, setOwnRows] = useState(null)
  const reset = () => { setDraft(initialDraft(a, type)); setPatientPick(''); setTouched(false) }
  // Only a saved patient appointment can become a treatment — not a draft, not a
  // doctor block, and not a visit the patient cancelled or never showed up for.
  const canCreateTreatment = !isCreate && !isBlock && !!a.id && !!a.patient_id
    && a.status !== 'X' && a.status !== 'N' && !!onTreatmentCreated

  /**
   * The status the SERVER lands on when this save takes away the patient's last
   * future booking — i.e. what Xano trigger 2's demote arm writes.
   *
   * It restores `patient.status_prev` when that holds a recall queue (the patient
   * was pulled out of it by booking, so losing the booking puts them back), and
   * writes "Error" when there is nothing to go back to. A MIRROR of the trigger —
   * see knowledge/xano/backups/trigger2_patient-status_2026-09-13_AFTER.xs; change
   * one and change the other, or this dialog tells staff the wrong thing.
   *
   * Reads as "Error" on an older row (or before `status_prev` exists at all),
   * which is exactly the behaviour that preceded the stash.
   */
  const statusPrev = String(patientInfo?.status_prev || '').trim()
  const fallbackStatus = statusPrev.startsWith('Recall') ? statusPrev : 'Error'

  // The patient's appointments the prediction runs over: the caller's list when it
  // has one (the patient page), else the drawer's own fetch below.
  const sibs = Array.isArray(siblings) ? siblings : (Array.isArray(ownRows) ? ownRows : null)

  /**
   * What this UNSAVED edit will do to the patient's own status — the right half
   * of the สถานะ row (Ram, 2026-09-19: สถานะนัด on the left drives สถานะคนไข้ on the
   * right, which either follows by itself or says a choice is owed).
   *
   *   { cur, next, hasBooking }
   *     cur         the patient's status now
   *     next        what the SERVER will land on after this save (trigger 2, via
   *                 predictPatientStatus — a mirror, not a decision)
   *     hasBooking  will a qualifying future booking remain after this save?
   *
   * THE RULE THE PANEL FOLLOWS:
   *   • a booking remains → the system decides (มีนัดแล้ว, or the case type it
   *     never promotes over). Read-only — anything else would be overwritten by
   *     the next appointment write anyway.
   *   • no booking remains → staff decide, starting from `next`. When `next` is
   *     Error (nothing stashed to fall back to) there is no default: Error means
   *     "a human must look at this", and a human is looking. So the save waits
   *     for a pick — this is what the post-save question dialog used to ask, now
   *     asked BEFORE the save, beside the field that causes it.
   *
   * 💸 Pure arithmetic on rows already in memory — no request per keystroke, and
   * ยกเลิก leaves nothing to undo. The clinic calendar has no per-patient list, so
   * there the drawer fetches one on open (the same request finishSave makes).
   * `null` = cannot tell yet (loading / failed) — then the panel stays quiet and
   * finishSave's post-save check is the safety net, as before.
   */
  const patientOutlook = useMemo(() => {
    if (isBlock || !patientInfo || !Array.isArray(sibs)) return null
    const [y, mo, d] = (draft.date || '').split('-').map(Number)
    const start = (y && mo && d) ? bkkToMs(y, mo, d, Number(draft.hh), Number(draft.mm)) : startMs
    const edited = { ...a, status: draft.status, start_date: start, active: true }
    const after = isCreate
      ? [...sibs, edited]
      : sibs.map((r) => (String(r.id) === String(a.id) ? edited : r))
    const hasBooking = hasFutureBooking(after)
    const cur = String(patientInfo.status ?? '').trim()
    const next = predictPatientStatus({ current: cur, statusPrev: patientInfo.status_prev, hasBooking })
    return { cur, next, hasBooking }
  }, [isBlock, patientInfo, sibs, draft.status, draft.date, draft.hh, draft.mm, a, isCreate, startMs])

  // No booking left → staff decide. With no stashed recall to fall back on, they MUST.
  const pickMode = !!patientOutlook && !patientOutlook.hasBooking
  const mustPick = pickMode && (patientOutlook.next === 'Error' || patientOutlook.next === '')
  const patientChoice = pickMode ? (patientPick || (mustPick ? '' : patientOutlook.next)) : ''
  const needsPatientPick = mustPick && !patientPick
  // Only a choice that differs from what the server lands on by itself needs a write.
  const patientWrite = pickMode && patientChoice && patientChoice !== patientOutlook.next ? patientChoice : null

  // Same prediction for ลบ — a soft-delete takes the booking away just as surely
  // as cancelling it, and that is the version staff are least likely to expect.
  const deletedPatientStatus = useMemo(() => {
    if (isBlock || isCreate || !patientInfo || !Array.isArray(sibs)) return null
    const after = sibs.map((r) => (String(r.id) === String(a.id) ? { ...r, active: false } : r))
    const next = predictPatientStatus({
      current: patientInfo.status,
      statusPrev: patientInfo.status_prev,
      hasBooking: hasFutureBooking(after),
    })
    return next === String(patientInfo.status ?? '').trim() ? null : next
  }, [isBlock, isCreate, patientInfo, sibs, a])

  // Load the patient — for the header card (edit) and the สถานะคนไข้ panel (both
  // modes; on the clinic calendar's create, once a patient has been picked).
  useEffect(() => {
    setPatientInfo(null)
    if (!patientId) return
    let alive = true
    apiGet(`/api/patients/${patientId}`).then((p) => { if (alive && p?.id) setPatientInfo(p) }).catch(() => {})
    return () => { alive = false }
  }, [patientId])

  // Their appointments, when the caller did not hand them in. A failure is `false`,
  // never [] — an empty list would read as "no bookings" and demand a status pick.
  useEffect(() => {
    setOwnRows(null)
    if (Array.isArray(siblings) || !patientId) return
    let alive = true
    apiGet('/api/appointments', { patient_id: patientId, type: 'patient' })
      .then((r) => { if (alive) setOwnRows(asList(r).filter((x) => String(x.patient_id) === String(patientId))) })
      .catch(() => { if (alive) setOwnRows(false) })
    return () => { alive = false }
  }, [siblings, patientId])

  // The type catalog is lazy-loaded when the drawer opens, so the very first
  // render always sees an empty lookup and cannot resolve appointmentlist_id →
  // ประเภท. Fill it in when the catalog lands, or a booking made from a calendar
  // slot (which arrives with a treatId already chosen) and every edit would sit
  // on a blank ประเภท with a รายละเอียด list that never matches.
  useEffect(() => {
    const rec = draft.appointmentlist_id ? type.byId?.[String(draft.appointmentlist_id)] : null
    if (rec?.type && rec.type !== draft.typeName) setDraft((d) => ({ ...d, typeName: rec.type }))
  }, [type, draft.appointmentlist_id]) // eslint-disable-line react-hooks/exhaustive-deps

  // Doctor options are derived from the day's timetable for the chosen branch+date.
  useEffect(() => {
    if (!draft.branch_id || !draft.date) { setTtRows([]); return }
    let alive = true; setTtLoading(true)
    apiGet('/api/appointments', { type: 'timetable', branch_id: draft.branch_id, start_date: draft.date, end_date: draft.date })
      .then((r) => { if (alive) { setTtRows(asList(r)); setTtLoading(false) } })
      .catch(() => { if (alive) { setTtRows([]); setTtLoading(false) } })
    return () => { alive = false }
  }, [draft.branch_id, draft.date])

  // Tooth Taxi (mobile clinic): every appointment starts at the session start —
  // default the time to that day's timetable window start for this branch. Only
  // as a DEFAULT: a booking opened by clicking a calendar slot already carries
  // the time that was clicked (a.start_date), and stomping it back to the window
  // start sent every slot click to 08:00.
  const isToothTaxi = String(draft.branch_id) === '9' || /tooth\s*taxi/i.test(branch.map?.[String(draft.branch_id)] || '')
  useEffect(() => {
    if (!isCreate || !isToothTaxi || !draft.date || startMs != null) return
    const starts = ttRows
      .filter((r) => r.event_type === 'inverse-background' && r.block !== true)
      .map((r) => msOf(r.start_date))
      .filter((v) => v != null)
    if (!starts.length) return
    const hm = fmtTime(Math.min(...starts))
    setDraft((d) => (d.hh === hm.slice(0, 2) && d.mm === hm.slice(3, 5)) ? d : { ...d, hh: hm.slice(0, 2), mm: hm.slice(3, 5) })
  }, [isToothTaxi, ttRows, draft.date, isCreate, startMs]) // eslint-disable-line react-hooks/exhaustive-deps

  const set = (k, v) => edit((d) => ({ ...d, [k]: v }))

  // When a doctor is pre-selected from a calendar slot, we flip the cascade:
  // filter the treatment type/detail to what THAT doctor can do (spec − except,
  // honoring the shift's custom_spec/custom_except overrides), and keep the doctor
  // fixed. Otherwise it's the normal type→detail→doctor flow.
  const doctorLocked = isCreate && !!a.doctor_id
  const docShiftRows = useMemo(
    () => (doctorLocked ? ttRows.filter((r) => String(r.doctor_id) === String(draft.doctor_id) && r.event_type === 'inverse-background' && r.block !== true) : []),
    [doctorLocked, ttRows, draft.doctor_id],
  )
  const trOK = (tr) => {
    if (!doctorLocked || !draft.doctor_id) return true
    if (docShiftRows.length) return docShiftRows.some((row) => shiftEligible(row, tr, docInfo))
    return doctorEligible(draft.doctor_id, tr, docInfo)
  }

  /* ── การรักษา: one searchable, grouped picker ───────────────────────────────
   * Replaces the ประเภท → รายละเอียด cascade with the same control the booking
   * calendar's filter uses (lib/apptTypes.js). Unlike that filter this must land on
   * a CONCRETE treatment — appointmentlist_id is required to save — so there are no
   * per-type "ทั้งหมด" rows here: every option carries an id.
   * `trOK` still applies: when a doctor came from a calendar slot the list is
   * narrowed to what THAT doctor may do, honouring the shift's custom overrides. */
  const treatOptions = []
  for (const t of (type.types || [])) {
    if (!trOK({ type: t })) continue
    for (const o of (type.byType?.[t] || [])) {
      if (!trOK({ type: t, detail: o.detail, id: o.id })) continue
      const value = treatmentLabel(t, o.detail)
      treatOptions.push({ value, label: o.detail, selectedLabel: value, group: t, id: String(o.id), duration: o.duration })
    }
  }
  // The appointment's OWN treatment, when the list above does not contain it: a
  // retired catalogue row (id 53 and its 2,820 bookings), or one a locked doctor may
  // not do. Without this the drawer would open showing a DIFFERENT treatment and
  // save that over it. Ungrouped and first, so it never splits a heading in two, and
  // labelled in full because it has no heading above it to supply the type.
  const curRec = draft.appointmentlist_id ? type.byId?.[String(draft.appointmentlist_id)] : null
  const curValue = !draft.appointmentlist_id
    ? ''
    : curRec
      ? treatmentLabel(curRec.type, curRec.detail)
      : (apptType(a, '').detail || apptType(a, '').main || String(draft.appointmentlist_id))
  if (draft.appointmentlist_id && !treatOptions.some((o) => o.id === String(draft.appointmentlist_id))) {
    treatOptions.unshift({ value: curValue, label: curValue, group: '', id: String(draft.appointmentlist_id), duration: curRec?.duration || 0 })
  }
  const treatByValue = new Map(treatOptions.map((o) => [o.value, o]))
  // Changing the treatment resets the doctor (the eligible list depends on it),
  // unless the doctor is locked in from a calendar slot.
  function onTreat(value) {
    const o = treatByValue.get(value)
    if (!o) return
    edit((d) => ({
      ...d,
      typeName: o.group || curRec?.type || d.typeName,
      appointmentlist_id: o.id,
      duration: o.duration || d.duration,
      ...(doctorLocked ? {} : { doctor_id: '' }),
    }))
  }
  // Branch options, id order, สนามเป้า (#1) never offered (kit/branches.js,
  // decision #2). An existing row still saved under #1 keeps that value — it is
  // only re-sent when staff pick another branch — and reads as อารีย์, with a hint
  // so it is not mistaken for a second อารีย์ in the list.
  const branchOptions = formBranchOptions(lookupBranches(branch))
  if (String(draft.branch_id) === '1') {
    branchOptions.unshift({ value: 1, label: branchLabel(1, branch) || 'อารีย์', hint: 'บันทึกไว้เป็นสนามเป้า (สำนักงานใหญ่)' })
  }
  const opts = (list, label = (v) => String(v)) => list.map((v) => ({ value: String(v), label: label(v) }))
  const hourOptions = opts(withItem(HOURS, draft.hh).sort())
  const minuteOptions = opts(withItem(MINUTES, draft.mm).sort())
  const durationOptions = opts(withItem(DURATIONS, Number(draft.duration)).sort((x, y) => x - y), (m) => `${m} นาที`)

  // Chosen treatment (type + detail) from the selected appointmentList.
  const treatment = useMemo(() => {
    const rec = draft.appointmentlist_id ? type.byId?.[String(draft.appointmentlist_id)] : null
    return rec ? { id: String(draft.appointmentlist_id), type: rec.type, detail: rec.detail } : null
  }, [draft.appointmentlist_id, type])

  // Doctors eligible for the chosen treatment AND working that branch+date.
  const docOptions = useMemo(() => {
    const list = eligibleDoctorsFromTimetable(ttRows, treatment, docInfo)
    if (draft.doctor_id && !list.some((d) => d.id === String(draft.doctor_id))) {
      const info = docInfo[String(draft.doctor_id)]
      list.unshift({ id: String(draft.doctor_id), name: info?.code || (a.doctor && a.doctor.code) || `ทันตแพทย์ #${draft.doctor_id}`, img: info?.img || (a.doctor && a.doctor.profile_img) || '' })
    }
    return list
  }, [ttRows, treatment, draft.doctor_id, docInfo, a])

  // Everything except สถานะ / รายละเอียดนัดหมาย / หมายเหตุภายใน is required.
  // Create also requires a chosen patient. Block mode drops treatment + patient.
  const valid = isBlock
    ? !!(draft.branch_id && draft.doctor_id && draft.date && draft.hh && draft.mm && Number(draft.duration) > 0)
    : !!(draft.typeName && draft.appointmentlist_id && draft.branch_id && draft.doctor_id && draft.date && draft.hh && draft.mm && Number(draft.duration) > 0 && (!isCreate || patientId)) && !needsPatientPick

  async function save() {
    if (isCreate) {
      if (!isBlock && !patientId) { setErr('กรุณาเลือกคนไข้ก่อน'); return }
      // A brand-new booking has no ★ to preserve, so nothing may set one.
      if (!isBlock && !canSetApptStatus(draft.status, '')) { setErr('สถานะ ★ ตั้งเองไม่ได้ — ต้องให้คนไข้ยืนยันผ่าน SMS หรือ Patient Portal'); return }
      setSaving(true); setErr(null)
      try {
        const [y, mo, d] = (draft.date || '').split('-').map(Number)
        let start = null, end = null
        if (y && mo && d) { start = bkkToMs(y, mo, d, Number(draft.hh), Number(draft.mm)); end = start + (Number(draft.duration) || 0) * 60000 }
        const payload = isBlock
          ? {
              patient_id: null, status: '', appointmentlist_id: null,
              doctor_id: Number(draft.doctor_id) || null,
              branch_id: Number(draft.branch_id) || null,
              date: draft.date, start_date: start, end_date: end,
              duration: Number(draft.duration) || 0,
              details: draft.details, remark: draft.remark,
              event_type: '', block: true, active: true,
            }
          : {
              patient_id: Number(patientId),
              status: draft.status || '',
              // Start the 48h hold clock now (see data/appointmentStatuses.js).
              ...(draft.status === R48 ? { R48_at: Date.now() } : {}),
              appointmentlist_id: Number(draft.appointmentlist_id) || null,
              doctor_id: Number(draft.doctor_id) || null,
              branch_id: Number(draft.branch_id) || null,
              date: draft.date, start_date: start, end_date: end,
              duration: Number(draft.duration) || 0,
              from: draft.from, upsale: !!draft.upsale, extraQue: !!draft.extraQue,
              details: draft.details, remark: draft.remark,
              event_type: '', block: false, active: true,
            }
        await postJson('/api/appointments', payload)
        // The สถานะคนไข้ pick, after the booking exists (so after trigger 2). A
        // failure here cannot keep the drawer open — a second click would create a
        // second appointment — so the patient keeps what the server gave them,
        // which is exactly what happened before this panel existed.
        if (!isBlock && patientWrite) {
          try {
            await postJson(`/api/patients/status/${patientId}`, { status: patientWrite })
            onPatientStatus?.(patientWrite)
          } catch { /* see above */ }
        }
        onCreate?.()
        toast.success(isBlock ? 'บันทึกบล็อกเวลาแล้ว' : 'สร้างนัดหมายเรียบร้อยแล้ว')
      } catch (e) {
        failed(e?.message || (isBlock ? 'บันทึกบล็อกไม่สำเร็จ' : 'สร้างนัดหมายไม่สำเร็จ'))
      } finally {
        setSaving(false)
      }
      return
    }
    setSaving(true); setErr(null)
    let r48Stamp = null
    try {
      const [y, mo, d] = (draft.date || '').split('-').map(Number)
      let start = startMs, end = msOf(a.end_date)
      if (y && mo && d) {
        start = bkkToMs(y, mo, d, Number(draft.hh), Number(draft.mm))
        end = start + (Number(draft.duration) || 0) * 60000
      }
      // Editing an existing block — only schedule + reason, no status/treatment.
      if (isBlock) {
        await patchJson(`/api/appointments/${a.id}`, {
          date: draft.date, start_date: start, end_date: end, duration: Number(draft.duration) || 0,
          details: draft.details, remark: draft.remark,
          doctor_id: Number(draft.doctor_id) || a.doctor_id, branch_id: Number(draft.branch_id) || a.branch_id,
          block: true,
        })
        onSaved?.()
        toast.success('บันทึกแล้ว')
        return
      }
      if (draft.status !== (a.status || '')) {
        // ★ (C*/P*) is earned by the patient answering the SMS / portal — never
        // assigned by staff. The picker only ever offers it back to the row that
        // already holds it, so this is the belt to that brace.
        if (!canSetApptStatus(draft.status, a.status)) throw new Error('สถานะ ★ ตั้งเองไม่ได้ — ต้องให้คนไข้ยืนยันผ่าน SMS หรือ Patient Portal')
        await postJson(`/api/appointment-status/${a.id}`, { status: draft.status })
        // (Re)start the 48h hold clock the moment R48 is set, so a slot reserved
        // today isn't judged against a months-old created_at. Goes on the PATCH
        // below — the status endpoint only accepts status/event_color.
        if (draft.status === R48) r48Stamp = Date.now()
      }
      await patchJson(`/api/appointments/${a.id}`, {
        date: draft.date,
        start_date: start,
        end_date: end,
        duration: Number(draft.duration) || 0,
        from: draft.from,
        upsale: !!draft.upsale,
        extraQue: !!draft.extraQue,
        details: draft.details,
        remark: draft.remark,
        doctor_id: Number(draft.doctor_id) || a.doctor_id,
        branch_id: Number(draft.branch_id) || a.branch_id,
        appointmentlist_id: Number(draft.appointmentlist_id) || a.appointmentlist_id,
        ...(r48Stamp ? { R48_at: r48Stamp } : {}),
      })
      await finishSave({ decided: pickMode, write: patientWrite })
      toast.success('บันทึกแล้ว')
    } catch (e) {
      failed(e?.message || 'บันทึกไม่สำเร็จ')
    } finally {
      setSaving(false)
    }
  }

  // Errors: inline at the top of the drawer (it stays open to fix/retry) + a toast.
  function failed(msg) {
    setErr(msg)
    toast.error(msg)
  }

  /**
   * Close the drawer — unless this edit just took away the patient's LAST future
   * booking, in which case ask the person who did it what the status should be.
   *
   * WHY ASK HERE. Xano table trigger 2 already demotes the patient to "Error" the
   * moment the booking goes away, and that is the right answer when nobody is
   * around to ask — a patient tapping เลื่อนนัด from the SMS link, Bubble, or the
   * nightly sweep. But "Error" only means "a human must look at this", and here a
   * human IS looking: they just had the conversation and know whether this is a
   * 3-month recall or a closed case. Asking now is one step; leaving it to Error
   * means someone re-opens the file later and guesses.
   *
   * Gated on the patient currently being มีนัดแล้ว, which is exactly when trigger 2
   * would fire. Any other bucket (Recall, รากเทียม, ปกส, ยกเลิกเคส) is deliberate,
   * and the trigger no longer overwrites those either.
   *
   * The membership rule is `hasFutureBooking()` — a MIRROR of api 166 and trigger
   * 2, see the note on BOOKING_STATUSES. Re-read fresh rather than reasoning from
   * `draft`, because a move into the past or a soft-delete takes the booking away
   * without any status change at all.
   *
   * Never blocks the save: the write already succeeded, and if this check throws
   * the server-side trigger still writes Error. Worst case we skip the question.
   *
   * 2026-09-19: the question is now normally asked BEFORE the save, in the
   * สถานะคนไข้ panel. `decided` = the panel was in its pick state, so staff have
   * already answered and the dialog must not ask again; `write` = their answer,
   * when it differs from what trigger 2 lands on by itself. It is written AFTER
   * the appointment so it lands on top of the trigger, and only when the fresh
   * read agrees no booking survives — if in-memory rows were stale and one does,
   * the server's มีนัดแล้ว is right and a queue status would be wrong. The dialog
   * below remains the safety net for everything the panel could not see: ลบ, a
   * prediction still loading, stale rows.
   */
  async function finishSave({ decided = false, write = null } = {}) {
    const pid = a.patient_id
    const cur = patientInfo?.status
    if (!pid || (!decided && cur !== 'มีนัดแล้ว')) { onSaved?.(); return }
    let booked = null
    try {
      const rows = await apiGet('/api/appointments', { patient_id: pid, type: 'patient' })
      booked = hasFutureBooking(asList(rows).filter((r) => String(r.patient_id) === String(pid)))
    } catch { /* booked stays null — unknown */ }

    if (decided && booked !== true) {
      if (write) {
        try {
          await postJson(`/api/patients/status/${pid}`, { status: write })
        } catch (e) {
          // The appointment IS saved; only the patient status is not. Saving again
          // is safe (a PATCH, and the status write repeats), so say exactly that.
          throw new Error(`บันทึกนัดหมายแล้ว แต่เปลี่ยนสถานะคนไข้ไม่สำเร็จ — กดบันทึกอีกครั้ง${e?.message ? ` (${e.message})` : ''}`)
        }
        setPatientInfo((p) => (p ? { ...p, status: write, status_prev: '' } : p))
        onPatientStatus?.(write)
      } else if (cur === 'มีนัดแล้ว') {
        // Staff accepted the stashed recall trigger 2 just restored.
        setPatientInfo((p) => (p ? { ...p, status: fallbackStatus, status_prev: '' } : p))
        onPatientStatus?.(fallbackStatus)
      }
      onSaved?.()
      return
    }

    if (booked === false && cur === 'มีนัดแล้ว') {
      // Trigger 2 has just written `fallbackStatus` server-side. Say so on our
      // own screen now — if the prompt is dismissed, that is what the row holds.
      setPatientInfo((p) => (p ? { ...p, status: fallbackStatus, status_prev: '' } : p))
      onPatientStatus?.(fallbackStatus)
      setStatusPrompt(true)
      return
    }
    onSaved?.()
  }

  // Soft-delete: keep the row, just flag active:false (hidden everywhere).
  // The confirm stays open with `busy` while the request runs (no double send,
  // cannot be dismissed mid-delete), then closes either way.
  async function doDelete() {
    if (deleting) return
    setDeleting(true); setErr(null)
    try {
      await patchJson(`/api/appointments/${a.id}`, { active: false })
      await finishSave()
      toast.success('ลบนัดหมายแล้ว')
    } catch (e) {
      failed(e?.message || 'ลบนัดหมายไม่สำเร็จ')
      setDeleting(false)
    } finally {
      setConfirmDel(false)
    }
  }

  // The save is already committed — this is the follow-up question, so it replaces
  // the drawer rather than stacking on top of it. Dismissing does NOT undo anything:
  // it leaves whatever trigger 2 already wrote standing, which is the correct
  // fallback — see `fallbackStatus`.
  // มีนัดแล้ว is deliberately NOT offered — api 166 would refuse it (there is no
  // qualifying booking) and write Error anyway — and neither is Error itself, which
  // is what ข้าม already gives you.
  if (statusPrompt) {
    const pname = patientInfo?.fullname
      || [patientInfo?.name, patientInfo?.surname].filter(Boolean).join(' ').trim()
      || a._patient?.fullname || ''
    const groups = STATUS_GROUPS.filter((g) => g.label !== 'นัดหมาย' && g.label !== 'ระบบ')
    const pickStatus = async (s) => {
      setSaving(true); setErr(null)
      try {
        await postJson(`/api/patients/status/${a.patient_id}`, { status: s })
        setPatientInfo((p) => (p ? { ...p, status: s } : p))
        onPatientStatus?.(s)
        onSaved?.()
        toast.success('เปลี่ยนสถานะคนไข้แล้ว')
      } catch (e) {
        failed(e?.message || 'เปลี่ยนสถานะไม่สำเร็จ')
        setSaving(false)
      }
    }
    // Esc / backdrop / ✕ = ข้าม: the save is done, so closing never loses an edit.
    return (
      <Modal onClose={() => onSaved?.()} busy={saving}
        icon={CalendarX} iconTone="amber"
        title="ไม่มีนัดหมายในอนาคตแล้ว"
        subtitle={<>
          {pname ? <span className="font-medium text-slate-700">{pname}</span> : 'คนไข้รายนี้'} ไม่เหลือนัดที่ยืนยันได้ ·{' '}
          {fallbackStatus === 'Error'
            ? 'จะตั้งสถานะเป็นอะไร?'
            : <>ระบบคืนสถานะเป็น <span className="font-medium text-slate-700">{fallbackStatus}</span> ให้แล้ว · จะเปลี่ยนเป็นอย่างอื่นไหม?</>}
        </>}
        footer={(
          <div className="flex items-center gap-2">
            <div className="flex-1 text-[12px] text-slate-400">
              {fallbackStatus === 'Error'
                ? 'ถ้าข้าม ระบบจะตั้งเป็น Error ให้ตามไปแก้ทีหลัง'
                : `ถ้าข้าม จะคงสถานะ ${fallbackStatus} ไว้ตามเดิม`}
            </div>
            {saving && <Loader2 size={15} className="animate-spin text-slate-400" />}
            <Button variant="secondary" onClick={() => onSaved?.()} disabled={saving}>ข้าม</Button>
          </div>
        )}>
        {groups.map((g) => (
          <div key={g.label} className="mb-3">
            <div className="text-[11px] font-semibold text-slate-400 uppercase tracking-wide mb-1.5">{g.label}</div>
            <div className="flex flex-wrap gap-1.5">
              {g.items.map((s) => {
                const t = STATUS_TONES[statusTone(s)] || STATUS_TONES.none
                return (
                  <button key={s} type="button" disabled={saving} onClick={() => pickStatus(s)}
                    className={`px-2.5 py-1.5 rounded-lg text-[13px] font-medium ring-1 transition-all disabled:opacity-50 hover:brightness-95 ${t.pill}`}>
                    {s}
                  </button>
                )
              })}
            </div>
          </div>
        ))}
        <InlineError>{err}</InlineError>
      </Modal>
    )
  }

  const busy = saving || deleting
  const dirty = touched || !!patientPick || (canModeSelect && !!pickedPatient)
  // What the delete takes away, said in the confirm (not just "แน่ใจไหม").
  const delSummary = apptSummaryFields(a, { branch, patientName: patientInfo?.name || '' }).fields
    .map((f) => f.value).join(' · ')

  return (
    // Right-side Drawer, not a centered modal: the appointment list stays visible
    // behind it, so staff keep their place while reading one row. The kit owns
    // Esc / backdrop / ✕, refuses to close while saving, and asks before
    // throwing away typed edits.
    <Drawer
      width="lg"
      title={isBlock ? (isCreate ? 'บล็อกเวลา' : 'แก้ไขบล็อกเวลา') : (isCreate ? 'นัดหมายใหม่' : 'แก้ไขนัดหมาย')}
      onClose={onClose} busy={busy} dirty={dirty}
      // The record itself — #id · สร้าง · แก้ไข, pinned bottom-left. Create passes nothing.
      record={isCreate ? null : a} recordTable="appointment"
      footer={(
        <DrawerFooter
          onCancel={onClose} onSave={save} saving={saving} canSave={valid && !deleting}
          saveLabel={isBlock ? 'บันทึกบล็อก' : (isCreate ? 'สร้างนัดหมาย' : 'บันทึก')}
          status={valid ? null : needsPatientPick ? 'เลือกสถานะคนไข้ก่อนบันทึก' : 'กรอกข้อมูลที่จำเป็น (*) ให้ครบ'}
          statusTone={needsPatientPick ? 'error' : 'muted'}
          danger={!isCreate && a.id ? (
            <Button variant="danger-soft" size="sm" icon={Trash2} loading={deleting} disabled={saving}
              onClick={() => setConfirmDel(true)} title="ลบนัดหมาย (ตั้งค่า active = false)">ลบ</Button>
          ) : null}
          secondary={(
            <>
              <Button variant="ghost" size="sm" icon={RotateCcw} onClick={reset} disabled={busy}
                title={isCreate ? 'ล้างค่าที่กรอก' : 'คืนค่าตามที่บันทึกไว้ในระบบ'}>
                {isCreate ? 'ล้างค่า' : 'คืนค่าเดิม'}
              </Button>
              {/* สร้างการรักษา — the second entry point, and the ONLY one on the
                  clinic-wide /appointments page (calendar blocks are too small).
                  `existingCount` is only known on the patient page; elsewhere it
                  is 0 and the server's created:false answer raises the re-confirm. */}
              {canCreateTreatment && (
                <CreateTreatmentButton
                  variant="modal"
                  appointmentId={a.id}
                  existingCount={existingCount}
                  {...apptSummaryFields(a, { branch, patientName: patientInfo?.name || '' })}
                  onCreated={(res) => onTreatmentCreated?.(res)}
                  disabled={busy}
                />
              )}
            </>
          )}
        />
      )}
    >
      <div className="space-y-5">
        {/* Mode chooser — clinic-schedule create only */}
        {canModeSelect && (
          <div className="grid grid-cols-3 gap-2">
            {[{ k: 'existing', l: 'คนไข้เดิม', Icon: Search }, { k: 'new', l: 'คนไข้ใหม่', Icon: UserPlus }, { k: 'block', l: 'บล็อกเวลา', Icon: Ban }].map(({ k, l, Icon }) => {
              const on = entryMode === k
              return (
                <button key={k} type="button" onClick={() => { setEntryMode(k); setPickedPatient(null) }}
                  className={`flex items-center justify-center gap-1.5 px-2 py-2.5 rounded-xl text-[13.5px] font-semibold ring-1 transition-all ${on ? 'bg-brand-600 text-white ring-brand-600 shadow-sm shadow-brand-600/25' : 'bg-white text-slate-600 ring-slate-200 hover:bg-slate-50 hover:ring-slate-300'}`}>
                  <Icon size={15} strokeWidth={2} /> {l}
                </button>
              )
            })}
          </div>
        )}

        {/* The patient card and the create-mode picker scroll with the form:
            QuickAddPatient is a whole form and pinning it would eat the drawer. */}
        {!isCreate && a.patient_id ? <PatientCard info={patientInfo} embed={a._patient} title={a.title} patientId={a.patient_id} /> : null}
        {isCreate && !a.patient_id && !isBlock && (
          pickedPatient
            ? <PatientCard info={pickedPatient} patientId={pickedPatient.id} onChange={() => setPickedPatient(null)} />
            : entryMode === 'new'
              ? <QuickAddPatient branch={branch} defaultBranchId={draft.branch_id} onCreated={setPickedPatient} />
              : <PatientPicker onPick={setPickedPatient} />
        )}

        {err && <ErrorState title={isCreate ? 'บันทึกไม่สำเร็จ' : 'ทำรายการไม่สำเร็จ'} message={err} />}

        {/* งาน Lab — what the flask flag on the row/calendar is about. Edit only:
            a booking being created has no lab yet. Renders nothing when there is none. */}
        {!isCreate && !isBlock && a.id ? <ApptLabPanel appt={a} index={labIndex} /> : null}

        {!isBlock && (
          <FormSection title="การรักษา" icon={Stethoscope}>
            {/* One searchable, grouped treatment picker (was ประเภท → รายละเอียด) */}
            <Field label="ประเภท / รายละเอียด" required>
              <SearchSelect
                value={curValue}
                onChange={onTreat}
                options={treatOptions}
                disabled={!treatOptions.length}
                placeholder="เลือกการรักษา"
                searchPlaceholder="ค้นหาการรักษา…"
              />
            </Field>
          </FormSection>
        )}

        <FormSection title={isBlock ? 'ช่วงเวลาที่บล็อก' : 'วันและเวลา'} icon={CalendarDays}>
          {/* Branch · doctor · date */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <Field label="สาขา" required>
              <Select value={draft.branch_id ? String(draft.branch_id) : ''} options={branchOptions} placeholder="— เลือกสาขา —"
                onChange={(v) => edit((d) => ({ ...d, branch_id: String(v ?? ''), doctor_id: '' }))} />
            </Field>
            <Field label="วันที่" required>
              <EligibleDatePicker value={draft.date} branchId={draft.branch_id} treatment={treatment} docInfo={docInfo}
                onChange={(date) => edit((d) => ({ ...d, date, doctor_id: '' }))} />
            </Field>
            <Field label="ทันตแพทย์" required>
              <DoctorSelect value={draft.doctor_id ? String(draft.doctor_id) : ''} options={docOptions} docInfo={docInfo}
                disabled={!draft.branch_id || !draft.date} loading={ttLoading}
                placeholder={(!draft.branch_id || !draft.date) ? 'เลือกสาขา/วันที่ก่อน' : (docOptions.length ? 'เลือกทันตแพทย์' : 'ไม่มีทันตแพทย์ที่ตรงเงื่อนไข')}
                onChange={(id) => set('doctor_id', id)} />
            </Field>
          </div>

          {/* Time · duration */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
            <Field label="เวลา" required>
              <div className="flex items-center gap-1.5">
                <Select className="flex-1" value={draft.hh} options={hourOptions} minMenuWidth={96} onChange={(v) => set('hh', v)} />
                <span className="text-slate-400 font-medium">:</span>
                <Select className="flex-1" value={draft.mm} options={minuteOptions} minMenuWidth={96} onChange={(v) => set('mm', v)} />
              </div>
            </Field>
            <Field label="ระยะเวลา" required>
              <Select value={String(Number(draft.duration))} options={durationOptions} onChange={(v) => set('duration', Number(v))} />
            </Field>
          </div>
        </FormSection>

        {/* สถานะนัด → สถานะคนไข้, side by side (Ram, 2026-09-19). Staff set the
            LEFT; the RIGHT follows it, or says a choice is owed. See
            patientOutlook for the rule. */}
        {!isBlock && (
          <FormSection title="สถานะ" icon={ArrowLeftRight}>
            <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto_1fr] gap-x-2 gap-y-4 items-start">
              <Field label="สถานะนัด">
                {/* `original` = the SAVED status, so a patient-confirmed row shows
                    its own ★ status (locked) instead of "— ไม่ระบุ —". */}
                <ApptStatusSelect className={FIELD_CLASS} value={draft.status}
                  original={isCreate ? '' : (a.status || '')}
                  onChange={(code) => set('status', code)} />
              </Field>
              <ArrowRight size={16} className="hidden sm:block mt-[34px] text-slate-300" />
              <Field label="สถานะคนไข้" required={mustPick}>
                <PatientStatusPanel
                  hasPatient={!!patientId}
                  loading={!!patientId && !patientOutlook && ownRows !== false}
                  outlook={patientOutlook} pickMode={pickMode} mustPick={mustPick}
                  value={patientChoice} picked={!!patientPick}
                  onPick={setPatientPick} disabled={saving} />
              </Field>
            </div>
          </FormSection>
        )}

        {!isBlock && (
          <FormSection title="ที่มาและตัวเลือก" icon={Tag}>
            {/* Source · flags */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 items-end">
              <Field label="ที่มา">
                <Select value={draft.from} options={opts(withItem(SOURCES, draft.from))} onChange={(v) => set('from', v)} />
              </Field>
              {/* Yes/no form fields → kit Checkbox. pb-2.5 sits the box on the
                  ที่มา select's text line (the grid aligns to the bottom). */}
              <Checkbox className="pb-2.5" label="Upsale" checked={draft.upsale} onChange={(v) => set('upsale', v)} />
              <Checkbox className="pb-2.5" label="เคสแทรก" checked={draft.extraQue} onChange={(v) => set('extraQue', v)} />
            </div>
          </FormSection>
        )}

        {isBlock ? (
          <FormSection title="เหตุผล" icon={FileText}>
            <Field label="เหตุผลการบล็อกเวลา">
              <Textarea rows={2} value={draft.details} onChange={(e) => set('details', e.target.value)} className="resize-y" placeholder="เช่น พักเที่ยง · ประชุม · ลา" />
            </Field>
          </FormSection>
        ) : (
          <FormSection title="บันทึก" icon={FileText}>
            <div className="space-y-4">
              <Field label="รายละเอียดนัดหมาย">
                <Textarea rows={2} value={draft.details} onChange={(e) => set('details', e.target.value)} className="resize-y" />
              </Field>
              <Field label={<span className="inline-flex items-center gap-1.5"><EyeOff size={12} className="text-slate-400" /> หมายเหตุภายใน</span>}
                hint="ไม่แสดงต่อคนไข้ — พนักงานและทันตแพทย์เห็น">
                <Textarea rows={2} value={draft.remark} onChange={(e) => set('remark', e.target.value)} className="resize-y" placeholder="ไม่แสดงต่อคนไข้ — พนักงานและทันตแพทย์เห็น" />
              </Field>
            </div>
          </FormSection>
        )}
      </div>

      {confirmDel && (
        <ConfirmDialog
          tone="danger"
          title="ลบนัดหมายนี้?"
          message={<>
            {delSummary || `นัดหมาย #${a.id}`} — จะหายจากปฏิทินและรายการนัดทุกหน้า
            {deletedPatientStatus && <> · สถานะคนไข้จะเปลี่ยนเป็น <span className="font-medium text-slate-800">{deletedPatientStatus}</span></>}
          </>}
          confirmLabel="ลบนัดหมาย"
          busy={deleting}
          onConfirm={doDelete}
          onCancel={() => setConfirmDel(false)}
        />
      )}
    </Drawer>
  )
}

// Ensure `cur` is present in a list (so the current value always shows pre-load).
function withItem(list, cur) {
  if (cur == null || cur === '' || list.includes(cur)) return list
  return [cur, ...list]
}

// Outcome tally chip on the patient appointments header.
function StatChip({ label, value, tone = 'slate' }) {
  const TONES = {
    emerald: 'bg-emerald-50 text-emerald-700 ring-emerald-200',
    amber: 'bg-amber-50 text-amber-700 ring-amber-200',
    rose: 'bg-rose-50 text-rose-700 ring-rose-200',
    slate: 'bg-slate-100 text-slate-600 ring-slate-200',
  }
  return (
    <div className={`inline-flex items-center gap-2 rounded-xl px-3 py-1.5 ring-1 ${TONES[tone] || TONES.slate}`}>
      <span className="text-[18px] font-bold tabular-nums leading-none">{value}</span>
      <span className="text-[13px] font-semibold">{label}</span>
    </div>
  )
}

// Labelled group of fields inside the edit modal (visual hierarchy).
/**
 * The right half of the drawer's สถานะ row — what the patient's own status will be
 * after this save. Three states, following ApptEditDrawer's patientOutlook:
 *   follows    a booking remains, so the system decides — read-only pill
 *   pick       no booking remains — a picker, pre-set to where the server lands
 *   must pick  no booking remains and the server would land on Error — empty,
 *              ringed red, and the save waits for it
 */
function PatientStatusPanel({ hasPatient, loading, outlook, pickMode, mustPick, value, picked, onPick, disabled }) {
  const box = `${FIELD_CLASS} bg-slate-50 flex items-center gap-2 cursor-default`
  const note = (text, cls = 'text-slate-500') => <div className={`mt-1.5 text-[12px] leading-snug ${cls}`}>{text}</div>
  const pill = (s) => {
    const t = STATUS_TONES[statusTone(s)] || STATUS_TONES.none
    return <span className={`inline-flex items-center px-1.5 py-0.5 rounded-md text-[12px] font-medium ring-1 ${t.pill}`}>{s || '—'}</span>
  }

  if (!hasPatient) return <><div className={`${box} text-slate-400`}>—</div>{note('เลือกคนไข้ก่อน', 'text-slate-400')}</>
  if (!outlook) {
    return loading
      ? <div className={`${box} text-slate-400`}><Loader2 size={14} className="animate-spin" /> กำลังตรวจสอบ…</div>
      : <><div className={`${box} text-slate-400`}>—</div>{note('โหลดสถานะคนไข้ไม่ได้ — ระบบจะตรวจอีกครั้งตอนบันทึก', 'text-slate-400')}</>
  }

  const { cur, next } = outlook
  if (!pickMode) {
    const t = STATUS_TONES[statusTone(next)] || STATUS_TONES.none
    return (
      <>
        <div className={box} title="ยังมีนัดอยู่ — ระบบตั้งสถานะคนไข้ให้เอง">
          <span className={`w-2 h-2 rounded-full flex-none ring-1 ring-black/5 ${t.dot}`} />
          <span className="truncate">{next || '—'}</span>
          <span className="ml-auto text-[11.5px] text-slate-400 flex-none">อัตโนมัติ</span>
        </div>
        {next === cur
          ? note('ยังมีนัดอยู่ · ไม่เปลี่ยน', 'text-slate-400')
          : note(<>เปลี่ยนจาก {pill(cur)} ให้เอง</>)}
      </>
    )
  }

  return (
    <>
      <PatientStatusSelect className={FIELD_CLASS} value={value} onChange={onPick} disabled={disabled}
        invalid={mustPick && !picked} />
      {mustPick && !picked
        ? note('ต้องเลือก — ไม่เหลือนัดในอนาคต ระบบตัดสินแทนไม่ได้', 'text-rose-600 font-medium')
        : !picked && cur === 'มีนัดแล้ว'
          ? note(<>ไม่เหลือนัด · ระบบคืนเป็น {pill(next)} ให้ — เปลี่ยนได้</>)
          : value === cur
            ? note('ไม่เหลือนัด · ไม่เปลี่ยน — เปลี่ยนได้', 'text-slate-400')
            : note(<>จะเปลี่ยนจาก {pill(cur)}</>)}
    </>
  )
}

function FormSection({ title, icon: Icon, children }) {
  return (
    <div>
      <div className="flex items-center gap-1.5 mb-2.5 text-[12px] font-semibold uppercase tracking-[0.08em] text-slate-400">
        {Icon && <Icon size={13} strokeWidth={2} />}{title}
      </div>
      {children}
    </div>
  )
}

// Patient summary header shown at the top of the edit modal — name, LINE photo
// (or initial), HN, and case-status. `info` = full patient (fetched); `embed` =
// the appointment's _patient (immediate, may lack photo/status).
// Patient search picker (main-page "คนไข้เดิม" mode) — reuses /api/patients search.
function PatientPicker({ onPick }) {
  const [q, setQ] = useState('')
  const [results, setResults] = useState([])
  const [loading, setLoading] = useState(false)
  const debounceRef = useRef(null)
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current)
    const query = q.trim()
    if (query.length < MIN_PATIENT_SEARCH) { setResults([]); setLoading(false); return }
    setLoading(true)
    debounceRef.current = setTimeout(async () => {
      try {
        const res = await apiGet('/api/patients', { search: query, per_page: 8 })
        setResults(Array.isArray(res?.items) ? res.items : (Array.isArray(res) ? res : []))
      } catch { setResults([]) } finally { setLoading(false) }
    }, 220)
    return () => clearTimeout(debounceRef.current)
  }, [q])

  const ta = useTypeahead({
    count: results.length,
    onSelect: (i) => onPick(results[i]),
    onClose: () => setQ(''),
  })

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4">
      <Field label="เลือกคนไข้" required>
        <div className="relative">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
          <Input autoFocus value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={ta.onKeyDown} placeholder="ค้นหา ชื่อ · นามสกุล · HN · เบอร์โทร"
            className="pl-9 pr-9" />
          {loading && <Loader2 size={15} className="absolute right-3 top-1/2 -translate-y-1/2 text-brand-500 animate-spin" />}
        </div>
      </Field>
      {q.trim().length > 0 && q.trim().length < MIN_PATIENT_SEARCH && (
        <div className="mt-2 px-1 text-[12.5px] text-slate-400">พิมพ์อย่างน้อย {MIN_PATIENT_SEARCH} ตัวอักษรเพื่อค้นหา</div>
      )}
      {q.trim().length >= MIN_PATIENT_SEARCH && (
        <div ref={ta.listRef} className="mt-2 rounded-xl border border-slate-200 bg-white overflow-hidden max-h-56 overflow-y-auto">
          {results.length === 0 && !loading && <div className="px-3 py-2.5 text-[13.5px] text-slate-400">ไม่พบคนไข้ — ลองสลับไปโหมด "คนไข้ใหม่"</div>}
          {results.map((p, i) => {
            const name = [p.prefix, p.name, p.surname].filter(Boolean).join(' ').trim() || p.fullname || '—'
            const nick = p.nickname ? ` (${p.nickname})` : ''
            return (
              <button key={p.id} type="button" data-ti-index={i} onMouseEnter={() => ta.setActiveIndex(i)} onClick={() => onPick(p)}
                className={`w-full text-left px-3 py-2 flex items-center gap-2.5 hover:bg-brand-50 transition-colors border-b border-slate-50 last:border-0 ${i === ta.activeIndex ? 'bg-brand-50' : ''}`}>
                <span className="w-8 h-8 rounded-full overflow-hidden ring-1 ring-slate-200 bg-slate-100 flex items-center justify-center text-slate-500 text-[12.5px] font-semibold flex-none">
                  {p.line_picture_url ? <img src={p.line_picture_url} alt="" referrerPolicy="no-referrer" className="w-full h-full object-cover" /> : nameInitial(name)}
                </span>
                <span className="min-w-0">
                  <span className="block text-[14px] font-medium text-slate-800 truncate">{name}<span className="text-slate-400 font-normal">{nick}</span></span>
                  <span className="block text-[12.5px] text-slate-500 truncate">{[p.HN && `HN ${p.HN}`, p.phone].filter(Boolean).join(' · ') || '—'}</span>
                </span>
              </button>
            )
          })}
        </div>
      )}
    </div>
  )
}

// Inline quick-add ("คนไข้ใหม่" mode) — minimal fields to create a patient + assign HN, then select.
function QuickAddPatient({ branch, defaultBranchId, onCreated }) {
  const [name, setName] = useState('')
  const [surname, setSurname] = useState('')
  const [nickname, setNickname] = useState('')
  const [phone, setPhone] = useState('')
  const [email, setEmail] = useState('')
  const [branchId, setBranchId] = useState(defaultBranchId && String(defaultBranchId) !== '1' ? String(defaultBranchId) : '')
  const [saving, setSaving] = useState(false)
  const [err, setErr] = useState(null)
  const toast = useToast()
  const branchOptions = formBranchOptions(lookupBranches(branch)) // สนามเป้า (#1) never offered
  const valid = name.trim() && surname.trim() && branchId

  async function submit() {
    if (!valid || saving) return
    setSaving(true); setErr(null)
    try {
      const payload = { name: name.trim(), surname: surname.trim(), branch_id: Number(branchId) }
      if (nickname.trim()) payload.nickname = nickname.trim()
      if (phone.trim()) payload.phone = phone.trim()
      if (email.trim()) payload.email = email.trim()
      const p = await postJson('/api/patients/addnew', payload)
      if (!p?.id) throw new Error('ไม่ได้รับข้อมูลคนไข้ที่สร้าง')
      onCreated(p)
      toast.success('สร้างคนไข้ใหม่แล้ว')
    } catch (e) {
      const raw = e?.message || 'สร้างคนไข้ไม่สำเร็จ'
      // Surface the Xano message if it came back as "HTTP 400: {...}".
      let msg = raw
      const m = /\{.*\}/.exec(raw)
      if (m) { try { const j = JSON.parse(m[0]); msg = j.message || j.detail || raw } catch { /* keep raw */ } }
      setErr(msg)
      toast.error(msg)
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4">
      <div className="text-[13px] font-semibold text-slate-700 mb-2.5">ข้อมูลคนไข้ใหม่</div>
      {err && <div className="mb-2.5"><InlineError>{err}</InlineError></div>}
      <div className="grid grid-cols-2 gap-2.5">
        <QAField label="ชื่อ" req value={name} onChange={setName} />
        <QAField label="นามสกุล" req value={surname} onChange={setSurname} />
        <QAField label="ชื่อเล่น" value={nickname} onChange={setNickname} />
        <QAField label="เบอร์โทร" value={phone} onChange={(v) => setPhone(v.replace(/\D+/g, '').slice(0, 10))} inputMode="numeric" />
        <div className="col-span-2"><QAField label="อีเมล" value={email} onChange={setEmail} type="email" /></div>
        <Field label="สาขา" required className="col-span-2">
          <Select value={branchId} options={branchOptions} placeholder="— เลือกสาขา —" onChange={(v) => setBranchId(String(v ?? ''))} />
        </Field>
      </div>
      <div className="mt-4 flex justify-end">
        {/* A sub-form inside the booking drawer: creating the patient is its own
            step before the appointment, so it keeps its own button. */}
        <Button size="sm" icon={Plus} onClick={submit} loading={saving} disabled={!valid}>สร้างคนไข้ใหม่</Button>
      </div>
    </div>
  )
}

function QAField({ label, value, onChange, req, type = 'text', inputMode }) {
  return (
    <Field label={label} required={req}>
      <Input type={type} inputMode={inputMode} value={value} onChange={(e) => onChange(e.target.value)} />
    </Field>
  )
}

// The appointment surface's patient link lands on นัดหมาย (DECISIONS context
// 'appointment'), on its ประวัติ tab — the drawer already shows what is upcoming.
const PATIENT_APPT_TAB = 'appointments?tab=past'

function PatientCard({ info, embed, title, patientId, onChange }) {
  const src = info || embed || {}
  const name = [src.prefix, src.name, src.surname].filter(Boolean).join(' ').trim() || src.fullname || (title || '').trim() || '—'
  const nick = src.nickname ? ` (${src.nickname})` : ''
  const hn = src.HN || src.hn || ''
  const status = (src.status || '').trim()
  const tone = STATUS_TONES[statusTone(status)] || STATUS_TONES.none
  const img = info?.line_picture_url || ''
  return (
    <div className="flex items-center gap-3 rounded-xl border border-slate-200 bg-white px-4 py-3">
      <div className="w-11 h-11 rounded-full overflow-hidden ring-1 ring-slate-200 bg-white flex items-center justify-center flex-none text-slate-500">
        {img
          ? <img src={img} alt="" referrerPolicy="no-referrer" className="w-full h-full object-cover" onError={(e) => { e.currentTarget.style.display = 'none' }} />
          : <span className="text-[15px] font-semibold">{nameInitial(name)}</span>}
      </div>
      <div className="min-w-0 flex-1">
        {/* Name and HN open the patient (new tab) on their appointment history —
            the kit's patient link, appointment context. */}
        <div className="text-[15px] font-semibold text-slate-900 truncate">
          <PatientLink patientId={patientId} tab={PATIENT_APPT_TAB}>{name}</PatientLink>
          <span className="text-slate-400 font-normal">{nick}</span>
        </div>
        <div className="mt-1 flex items-center gap-2 flex-wrap">
          {hn && <PatientLink patientId={patientId} tab={PATIENT_APPT_TAB} className="text-[12.5px] text-slate-500 tabular-nums">HN {hn}</PatientLink>}
          {/* 🚨 READ-ONLY HERE, deliberately (2026-09-13). This used to be a live
              StatusControl, which made the drawer contain two writers of the SAME
              field under two different rules: this pill POSTs api 166 the instant
              you pick, while everything else in the dialog is a draft that commits
              on บันทึก. So picking here and then pressing ยกเลิก still saved, and
              picking here then บันทึก let trigger 2 overwrite the choice. The
              appointment status now drives this value — see the preview under
              สถานะ — and เปิดหน้าคนไข้ is where you change it on purpose. */}
          {patientId && (status
            ? <span title="สถานะคนไข้ · เปลี่ยนได้ที่หน้าคนไข้"
                className={`inline-flex items-center gap-1.5 rounded-full ring-1 px-2.5 py-1 text-[13px] font-semibold whitespace-nowrap ${tone.pill}`}>
                <span className={`w-1.5 h-1.5 rounded-full flex-none ${tone.dot}`} />{status}
              </span>
            : <span className="text-[12.5px] text-slate-400">ยังไม่มีสถานะ</span>)}
        </div>
      </div>
      {onChange
        ? <button type="button" onClick={onChange}
            className="inline-flex items-center gap-1.5 text-[13px] font-medium text-slate-600 bg-white hover:bg-slate-50 px-2.5 py-1.5 rounded-lg ring-1 ring-slate-200 flex-none">
            <RotateCcw size={13} /> เปลี่ยน
          </button>
        : patientId && (
          <PatientLink patientId={patientId} tab={PATIENT_APPT_TAB}
            className="inline-flex items-center gap-1.5 text-[13px] font-medium bg-white hover:bg-brand-50 hover:no-underline px-2.5 py-1.5 rounded-lg ring-1 ring-brand-200 flex-none">
            <ExternalLink size={14} /> เปิดหน้าคนไข้
          </PatientLink>
        )}
    </div>
  )
}

// Date picker that only lets you select days which have an eligible doctor for
// the chosen treatment at the chosen branch — the modal's "filtered calendar".
const TH_WEEK_MIN = ['จ', 'อ', 'พ', 'พฤ', 'ศ', 'ส', 'อา'] // Monday-first
function EligibleDatePicker({ value, branchId, treatment, docInfo, onChange }) {
  const [open, setOpen] = useState(false)
  const ref = useRef(null)
  const todayKey = bkkTodayISO()
  const init = (value || todayKey).split('-').map(Number)
  const [anchor, setAnchor] = useState({ y: init[0], m: init[1] - 1 })
  const [tt, setTt] = useState([])
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    function onDoc(e) { if (ref.current && !ref.current.contains(e.target)) setOpen(false) }
    document.addEventListener('mousedown', onDoc)
    return () => document.removeEventListener('mousedown', onDoc)
  }, [])

  useEffect(() => {
    if (!open || !branchId) return
    const { y, m } = anchor
    const start = `${y}-${pad(m + 1)}-01`
    const end = `${y}-${pad(m + 1)}-${pad(new Date(y, m + 1, 0).getDate())}`
    let alive = true; setLoading(true)
    apiGet('/api/appointments', { type: 'timetable', branch_id: branchId, start_date: start, end_date: end })
      .then((r) => { if (alive) { setTt(asList(r)); setLoading(false) } })
      .catch(() => { if (alive) { setTt([]); setLoading(false) } })
    return () => { alive = false }
  }, [open, branchId, anchor])

  const eligibleDays = useMemo(() => {
    const s = new Set()
    for (const row of tt) {
      if (row.event_type !== 'inverse-background' || row.block === true) continue
      if (!shiftEligible(row, treatment, docInfo)) continue
      const key = toISODate(msOf(row.start_date))
      if (key) s.add(key)
    }
    return s
  }, [tt, treatment, docInfo])

  const { y, m } = anchor
  const firstDow = (new Date(y, m, 1).getDay() + 6) % 7
  const daysInMonth = new Date(y, m + 1, 0).getDate()
  const cells = [...Array(firstDow).fill(null), ...Array.from({ length: daysInMonth }, (_, i) => i + 1)]
  const fmtSel = value ? fmtDateText(value, { weekday: true }) : ''
  const shift = (delta) => setAnchor((a) => { const dt = new Date(a.y, a.m + delta, 1); return { y: dt.getFullYear(), m: dt.getMonth() } })

  return (
    <div ref={ref} className="relative">
      <button type="button" onClick={() => setOpen((o) => !o)} className={`${FIELD_CLASS} flex items-center justify-between text-left`}>
        <span className={value ? 'text-slate-800' : 'text-slate-400'}>{fmtSel || 'เลือกวันที่'}</span>
        <ChevronDown size={15} className="text-slate-400 flex-none" />
      </button>
      {open && (
        <div className="absolute z-40 mt-1 w-[280px] rounded-xl border border-slate-200 bg-white shadow-lg shadow-slate-900/10 p-3">
          <div className="flex items-center justify-between mb-2">
            <button type="button" onClick={() => shift(-1)} className="w-7 h-7 flex items-center justify-center rounded-lg text-slate-500 hover:bg-slate-100"><ChevronLeft size={15} /></button>
            <div className="text-[13.5px] font-semibold text-slate-800">{fmtMonth(`${y}-${pad(m + 1)}-01`, { long: true })}</div>
            <button type="button" onClick={() => shift(1)} className="w-7 h-7 flex items-center justify-center rounded-lg text-slate-500 hover:bg-slate-100"><ChevronRight size={15} /></button>
          </div>
          <div className="grid grid-cols-7 text-center text-[10.5px] font-medium text-slate-400 mb-1">{TH_WEEK_MIN.map((d) => <div key={d}>{d}</div>)}</div>
          <div className="grid grid-cols-7 gap-0.5">
            {cells.map((d, i) => {
              if (d == null) return <div key={i} />
              const key = `${y}-${pad(m + 1)}-${pad(d)}`
              const ok = eligibleDays.has(key)
              const sel = key === value
              const isToday = key === todayKey
              return (
                <button key={i} type="button" disabled={!ok} onClick={() => { onChange(key); setOpen(false) }}
                  className={`h-8 rounded-lg text-[12.5px] font-medium transition-colors ${
                    sel ? 'bg-brand-600 text-white'
                    : !ok ? 'text-slate-300 cursor-not-allowed'
                    : isToday ? 'bg-brand-50 text-brand-700 ring-1 ring-brand-200'
                    : 'text-slate-700 hover:bg-slate-100'}`}>{d}</button>
              )
            })}
          </div>
          <div className="mt-2 text-[11.5px] text-center">
            {loading ? <span className="text-slate-400">กำลังโหลด…</span>
              : !branchId ? <span className="text-slate-400">เลือกสาขาก่อน</span>
              : eligibleDays.size === 0 ? <span className="text-slate-400">ไม่มีวันที่มีทันตแพทย์ตรงเงื่อนไขในเดือนนี้</span>
              : <span className="text-slate-400">เลือกได้เฉพาะวันที่มีทันตแพทย์ตรงเงื่อนไข</span>}
          </div>
        </div>
      )}
    </div>
  )
}

function DocAvatar({ img, name, size = 22 }) {
  if (img) return <img src={img} alt="" referrerPolicy="no-referrer" style={{ width: size, height: size }} className="rounded-full object-cover ring-1 ring-black/5 flex-none" />
  return <span style={{ width: size, height: size }} className="rounded-full bg-slate-200 text-slate-600 text-[11px] font-semibold flex items-center justify-center flex-none">{nameInitial(name)}</span>
}

// Doctor picker with profile photo / initial avatar.
function DoctorSelect({ value, options, docInfo, onChange, disabled, loading, placeholder }) {
  const [open, setOpen] = useState(false)
  const ref = useRef(null)
  useEffect(() => {
    function onDoc(e) { if (ref.current && !ref.current.contains(e.target)) setOpen(false) }
    document.addEventListener('mousedown', onDoc)
    return () => document.removeEventListener('mousedown', onDoc)
  }, [])
  const sel = value ? (options.find((o) => o.id === value) || { id: value, name: docInfo[value]?.code || `ทันตแพทย์ #${value}`, img: docInfo[value]?.img || '' }) : null
  return (
    <div ref={ref} className="relative">
      <button type="button" disabled={disabled} onClick={() => setOpen((o) => !o)}
        className={`${FIELD_CLASS} flex items-center gap-2 text-left ${disabled ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'}`}>
        {sel ? <><DocAvatar img={sel.img} name={sel.name} /><span className="truncate">{sel.name}</span></>
          : <span className="text-slate-400 truncate">{loading ? 'กำลังโหลด…' : placeholder}</span>}
        <ChevronDown size={15} className="ml-auto flex-none text-slate-400" />
      </button>
      {open && !disabled && (
        <div className="absolute z-30 mt-1 w-full max-h-64 overflow-y-auto rounded-xl border border-slate-200 bg-white shadow-lg shadow-slate-900/10 p-1">
          {options.length === 0 && <div className="px-2.5 py-2 text-[13.5px] text-slate-400">{loading ? 'กำลังโหลด…' : 'ไม่มีทันตแพทย์ที่ตรงเงื่อนไข'}</div>}
          {options.map((o) => (
            <button key={o.id} type="button" onClick={() => { onChange(o.id); setOpen(false) }}
              className={`w-full flex items-center gap-2 px-2 py-1.5 rounded-lg text-left text-[14px] hover:bg-slate-100 ${value === o.id ? 'bg-brand-50' : ''}`}>
              <DocAvatar img={o.img} name={o.name} />
              <span className="truncate flex-1">{o.name}</span>
              {value === o.id && <Check size={14} className="text-brand-600 flex-none" />}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}
